import { effectScope } from 'vue';
import store from '@/store';
import { FEMALE_FIRST_NAMES, MALE_FIRST_NAMES } from '@/utils/demoFirstNameSex';

// Fake first names come from the pool of the real name's sex (see
// fakeFirst), so prose keeps its pronouns: a real "Margaret" never becomes a
// male fake that a rewrite then calls "his". Unisex covers names that are
// ambiguous or unknown. The pools are disjoint from each other and from
// LAST_NAMES (no "Morgan", "Bailey"), so a fake never reads as a surname.
const FEMALE_FAKE_FIRST = [
  'Maria', 'Sarah', 'Emily', 'Rachel', 'Linda', 'Jennifer', 'Patricia',
  'Barbara', 'Susan', 'Karen', 'Nancy', 'Lisa', 'Betty', 'Dorothy', 'Sandra',
  'Ashley', 'Margaret', 'Donna', 'Carol', 'Ruth', 'Sharon', 'Michelle', 'Laura',
  'Kathleen', 'Amy',
];
const MALE_FAKE_FIRST = [
  'James', 'David', 'Michael', 'Robert', 'William', 'Richard', 'Thomas',
  'Daniel', 'Matthew', 'Anthony', 'Christopher', 'Andrew', 'Joseph', 'Charles',
  'Mark', 'Steven', 'Paul', 'Kevin', 'Brian', 'George', 'Timothy', 'Ronald',
  'Edward', 'Jason', 'Jeffrey',
];
const UNISEX_FAKE_FIRST = ['Jordan', 'Casey', 'Taylor', 'Riley', 'Avery', 'Quinn', 'Jamie', 'Dana'];

const FIRST_NAMES = [...FEMALE_FAKE_FIRST, ...MALE_FAKE_FIRST, ...UNISEX_FAKE_FIRST];

const LAST_NAMES = [
  'Chen', 'Whitfield', 'Martinez', 'Johnson', 'Anderson', 'Thompson', 'Garcia',
  'Robinson', 'Clark', 'Lewis', 'Walker', 'Hall', 'Allen', 'Young', 'King',
  'Wright', 'Hill', 'Scott', 'Adams', 'Baker', 'Nelson', 'Carter', 'Mitchell',
  'Perez', 'Roberts', 'Turner', 'Phillips', 'Campbell', 'Parker', 'Evans',
  'Edwards', 'Collins', 'Stewart', 'Morris', 'Reed', 'Cook', 'Morgan', 'Bell',
  'Murphy', 'Bailey', 'Rivera', 'Cooper', 'Richardson', 'Cox', 'Howard',
  'Ward', 'Torres', 'Peterson', 'Gray', 'Watson',
];

const PRACTICE_NAMES = [
  'Lakewood Primary Care', 'Summit Medical Group', 'Valley Health Associates',
  'Cedar Ridge Family Medicine', 'Riverside Medical Center', 'Maple Grove Clinic',
  'Evergreen Health Partners', 'Pinecrest Medical Associates', 'Beacon Hill Practice',
  'Clearwater Family Health', 'Harmony Medical Group', 'Oakdale Health Center',
  'Westside Primary Care', 'Northpoint Medical', 'Sunrise Health Partners',
  'Mountainview Clinic', 'Pacific Coast Medical', 'Heritage Family Practice',
  'Bayshore Health Group', 'Prairie Medical Associates', 'Crestview Health Center',
  'Harbor Medical Group', 'Meadowbrook Primary Care', 'Stonegate Family Medicine',
  'Bridgewater Health Partners', 'Fairview Medical Associates', 'Greenfield Clinic',
  'Crossroads Medical Group', 'Lakeshore Health Partners', 'Hillcrest Primary Care',
];

const FACILITY_NAMES = [
  'Memorial Regional Hospital', 'St. Joseph Medical Center', 'Community General Hospital',
  'Providence Health System', 'Good Samaritan Medical', 'Mercy Hospital',
  'United Health Center', 'Sacred Heart Hospital', 'Bayside Medical Center',
  'Summit Hospital', 'Northside Medical Center', 'Valley General Hospital',
  'Lakeview Regional', 'Trinity Health System', 'Riverside Community Hospital',
];

const PAYER_NAMES = [
  'Anthem Blue Cross', 'United Healthcare', 'Aetna', 'Cigna', 'Humana',
  'Kaiser Permanente', 'Blue Shield', 'Molina Healthcare', 'Centene',
  'WellCare', 'Magellan Health', 'Oscar Health', 'Bright Health',
];

const CONTRACT_NAMES = [
  'ACO REACH Global', 'ACO REACH Professional', 'MSSP Track 1+',
  'MSSP Enhanced Track', 'Direct Contracting Global', 'MA Value-Based',
  'Commercial Risk', 'Medicaid Managed Care', 'ACO REACH High Needs',
];

const STREET_NAMES = [
  'Oak', 'Maple', 'Cedar', 'Pine', 'Elm', 'Washington', 'Park', 'Lake',
  'Hill', 'Forest', 'River', 'Spring', 'Meadow', 'Sunset', 'Main',
];
const STREET_TYPES = ['St', 'Ave', 'Dr', 'Ln', 'Blvd', 'Ct', 'Rd', 'Way'];

const CITIES = [
  'Springfield', 'Riverside', 'Fairview', 'Georgetown', 'Madison',
  'Clinton', 'Franklin', 'Greenville', 'Bristol', 'Salem',
  'Manchester', 'Burlington', 'Ashland', 'Dayton', 'Oxford',
];

const STATES = [
  'AL', 'AZ', 'CA', 'CO', 'CT', 'FL', 'GA', 'IL', 'IN', 'IA',
  'KS', 'KY', 'LA', 'MA', 'MD', 'MI', 'MN', 'MO', 'NC', 'NJ',
  'NM', 'NY', 'OH', 'OR', 'PA', 'SC', 'TN', 'TX', 'VA', 'WA',
];

const PHARMACY_NAMES = [
  'CVS Pharmacy', 'Walgreens', 'Rite Aid', 'Walmart Pharmacy',
  'Kroger Pharmacy', 'Costco Pharmacy', 'Publix Pharmacy',
  'Safeway Pharmacy', 'Target Pharmacy', 'HEB Pharmacy',
];

const TENANT_NAMES = [
  'Pinnacle Health Network', 'Atlas Care Partners', 'Meridian Health Alliance',
  'Vanguard Medical Group', 'Keystone Health Partners', 'Sterling Health Systems',
  'Compass Care Network', 'Summit Health Collaborative', 'Apex Medical Partners',
  'Horizon Care Alliance',
];

// Brand mask: the customer brand "Vytalize"/"Vytal" appears in product copy,
// titles and names well outside tagged elements, so it is swapped for a
// neutral brand everywhere while Demo Mode is on (case preserved).
const BRAND_WORDS = { vytalize: 'Northstar', vytal: 'Vital' };
const BRAND_RE = /vytalize|vytal/gi;
const BRAND_TEST_RE = /vytal/i;

function matchCase(source, word) {
  if (source === source.toUpperCase()) return word.toUpperCase();
  if (source === source.toLowerCase()) return word.toLowerCase();
  return word;
}

function applyBrand(text) {
  if (!text || !BRAND_TEST_RE.test(text)) return text;
  return text.replace(BRAND_RE, (m) => matchCase(m, BRAND_WORDS[m.toLowerCase()]));
}

function djb2(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

// Bumped whenever a mapping below changes, so every fake moves together
// instead of some screens showing the old fake and some the new one.
export const SPOOF_MAPPING_VERSION = 2;

function normalizeValue(value) {
  return String(value).trim().replace(/\s+/g, ' ').toLowerCase();
}

// Structured fakes are a pure function of (mapping version, kind or role,
// normalized real value): no dependence on render order, no stored real->fake
// table, and the same fake after a reload or in another tab.
function seedFor(kind, value) {
  return djb2(`v${SPOOF_MAPPING_VERSION}|${kind}|${normalizeValue(value)}`);
}

// Deterministic generator for format-preserving substitution. Callers use the
// high bits; an LCG's low bits cycle.
function generator(seed) {
  let s = seed >>> 0 || 1;
  return (n) => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return (s >>> 16) % n;
  };
}

function pick(arr, seed) {
  return arr[seed % arr.length];
}

// A pool pick that is never the real value itself (a real "Chen" must not come
// back as "Chen", which would also read as unmasked).
function pickOther(arr, seed, real) {
  const avoid = normalizeValue(real);
  for (let i = 0; i < arr.length; i += 1) {
    const candidate = arr[(seed + i) % arr.length];
    if (candidate.toLowerCase() !== avoid) return candidate;
  }
  return arr[0];
}

function digitsOf(text) {
  return String(text).replace(/\D/g, '');
}

function spoofPhone(seed) {
  const area = 200 + (seed % 700);
  const mid = 200 + ((seed >> 8) % 700);
  const end = 1000 + ((seed >> 16) % 9000);
  return `(${area}) ${mid}-${end}`;
}

function spoofPhoneText(text) {
  return spoofPhone(seedFor('phone', digitsOf(text) || text));
}

// MBI: position 1 is 1-9, letters exclude S, L, O, I, B and Z. Alphanumeric
// positions get letters. Separators in the input are kept where they were.
const MBI_LETTERS = 'ACDEFGHJKMNPQRTUVWXY';
const MBI_PATTERN = 'CAANAANAANN';

function spoofMbiText(text, seed) {
  const next = generator(seed);
  const chars = [...MBI_PATTERN].map((slot) => {
    if (slot === 'C') return String(1 + next(9));
    if (slot === 'N') return String(next(10));
    return MBI_LETTERS[next(MBI_LETTERS.length)];
  });
  let i = 0;
  const lower = text === text.toLowerCase();
  const out = text.replace(/[A-Za-z0-9]/g, () => chars[i++]);
  return lower ? out.toLowerCase() : out;
}

// NPI: 10 digits starting with 1, last digit the Luhn check over 80840 + the
// first nine, so the fake passes the same validator a real one does.
function npiCheckDigit(nine) {
  const digits = `80840${nine}`;
  let sum = 0;
  let double = true;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let n = Number(digits[i]);
    if (double) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    double = !double;
  }
  return (10 - (sum % 10)) % 10;
}

function spoofNpi(seed, real = '') {
  for (let attempt = 0; attempt < 5; attempt += 1) {
    const next = generator(seed + attempt);
    let nine = '1';
    for (let i = 1; i < 9; i += 1) nine += String(next(10));
    const npi = nine + npiCheckDigit(nine);
    if (npi !== real) return npi;
  }
  return '1000000004';
}

function spoofTin(seed) {
  const next = generator(seed);
  let out = String(10 + next(90)) + '-';
  for (let i = 0; i < 7; i += 1) out += String(next(10));
  return out;
}

function spoofSsn(seed) {
  const next = generator(seed);
  let area = 100 + next(800);
  if (area === 666) area = 667;
  const group = String(10 + next(90));
  const serial = String(1000 + next(9000));
  return `${area}-${group}-${serial}`;
}

function spoofEmail(seed) {
  const first = pick(FIRST_NAMES, seed).toLowerCase();
  const last = pick(LAST_NAMES, seed >> 4).toLowerCase();
  const domains = ['gmail.com', 'yahoo.com', 'outlook.com', 'mail.com'];
  return `${first}.${last}@${pick(domains, seed >> 8)}`;
}

function spoofAddress(seed) {
  const num = 100 + (seed % 9900);
  const street = pick(STREET_NAMES, seed >> 4);
  const type = pick(STREET_TYPES, seed >> 8);
  const city = pick(CITIES, seed >> 12);
  const state = pick(STATES, seed >> 16);
  const zip = String(10000 + (seed % 90000));
  return `${num} ${street} ${type}, ${city}, ${state} ${zip}`;
}

// Address parts keep their shape: a street line stays a street line, a unit
// designator stays one, "City, ST 12345" stays a city line, a bare city or
// state stays a city or state. Anything else becomes a full fake address.
const UNIT_RE = /^(apt|apartment|suite|ste|unit|bldg|building|floor|fl|room|rm|#)(\.?\s*#?\s*)(\S+)$/i;
const CITY_LINE_RE = /^([A-Za-z][A-Za-z .'-]*),\s*([A-Za-z]{2})(\s+\d{5}(?:-\d{4})?)?$/;

function spoofAddressText(text) {
  const trimmed = text.trim();
  const seed = seedFor('address', trimmed);
  if (/^\d+\s+\S/.test(trimmed) && !trimmed.includes(',')) {
    return `${100 + (seed % 9900)} ${pick(STREET_NAMES, seed >> 4)} ${pick(STREET_TYPES, seed >> 8)}`;
  }
  let m = trimmed.match(UNIT_RE);
  if (m) return `${m[1]}${m[2]}${substituteChars(m[3], seed)}`;
  m = trimmed.match(CITY_LINE_RE);
  if (m) {
    const zip = m[3] ? ` ${spoofZipText(m[3])}` : '';
    return `${pickOther(CITIES, seed >> 12, m[1])}, ${pickOther(STATES, seed >> 16, m[2])}${zip}`;
  }
  if (/^[A-Za-z]{2}$/.test(trimmed)) return matchCase(trimmed, pickOther(STATES, seed, trimmed.toUpperCase()));
  if (/^[A-Za-z][A-Za-z .'-]*$/.test(trimmed) && trimmed.length <= 30) return pickOther(CITIES, seed, trimmed);
  return spoofAddress(seed);
}

function spoofZipText(text) {
  const trimmed = text.trim();
  const seed = seedFor('zip', trimmed.slice(0, 5));
  let zip = String(10000 + (seed % 90000));
  if (zip === trimmed.slice(0, 5)) zip = String(10000 + ((seed + 1) % 90000));
  return /-\d{4}$/.test(trimmed) ? `${zip}-${String(1000 + ((seed >> 8) % 9000))}` : zip;
}

// Legacy shape for date-like text that does not parse as a calendar date.
function spoofDate(seed) {
  const month = 1 + (seed % 12);
  const day = 1 + ((seed >> 4) % 28);
  const year = 1940 + ((seed >> 8) % 60);
  return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
}

// --- Dates ---
//
// Dates are parsed to a calendar date before hashing, so "01/15/1945" and
// "Jan 15, 1945" get the same fake, and the fake is written back in the
// input's own format.
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December',
];

function monthIndex(word) {
  const w = word.toLowerCase().replace(/\.$/, '');
  if (w.length < 3) return -1;
  return MONTH_NAMES.findIndex((m) => {
    const full = m.toLowerCase();
    return full === w || (w.length <= 4 && full.startsWith(w));
  });
}

function daysIn(y, m) {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

function validDate(c) {
  if (!c || c.m < 1 || c.m > 12 || c.d < 1 || c.d > daysIn(c.y, c.m)) return null;
  return c;
}

function todayParts() {
  const now = new Date();
  return { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() };
}

function parseDate(text) {
  let m = text.match(/^(\d{1,2})([/.-])(\d{1,2})\2(\d{4}|\d{2})$/);
  if (m) {
    let y = Number(m[4]);
    if (m[4].length === 2) y += y > todayParts().y % 100 ? 1900 : 2000;
    return validDate({
      y,
      m: Number(m[1]),
      d: Number(m[3]),
      fmt: { type: 'mdy', sep: m[2], padM: m[1].length === 2, padD: m[3].length === 2, yLen: m[4].length },
    });
  }
  m = text.match(/^(\d{4})-(\d{2})-(\d{2})((?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)(?:Z|[+-]\d{2}:?\d{2})?)?$/);
  if (m) {
    return validDate({ y: Number(m[1]), m: Number(m[2]), d: Number(m[3]), fmt: { type: 'iso', suffix: m[4] || '' } });
  }
  m = text.match(/^([A-Za-z]{3,9})(\.?)(\s+)(\d{1,2})(,?)(\s+)(\d{4})$/);
  if (m) {
    const idx = monthIndex(m[1]);
    if (idx < 0) return null;
    return validDate({
      y: Number(m[7]),
      m: idx + 1,
      d: Number(m[4]),
      fmt: { type: 'name', word: m[1], dot: m[2], s1: m[3], comma: m[5], s2: m[6], padD: m[4].length === 2 && m[4][0] === '0' },
    });
  }
  return null;
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function formatDate({ y, m, d }, fmt) {
  if (fmt.type === 'md') {
    return `${fmt.padM ? pad2(m) : m}${fmt.sep}${fmt.padD ? pad2(d) : d}`;
  }
  if (fmt.type === 'iso') return `${y}-${pad2(m)}-${pad2(d)}${fmt.suffix}`;
  if (fmt.type === 'name') {
    const full = MONTH_NAMES[m - 1];
    const word = matchCase(fmt.word, fmt.word.length <= 4 ? full.slice(0, 3) : full);
    return `${word}${fmt.dot}${fmt.s1}${fmt.padD ? pad2(d) : d}${fmt.comma}${fmt.s2}${y}`;
  }
  const mm = fmt.padM ? pad2(m) : String(m);
  const dd = fmt.padD ? pad2(d) : String(d);
  const yy = fmt.yLen === 2 ? pad2(y % 100) : String(y);
  return `${mm}${fmt.sep}${dd}${fmt.sep}${yy}`;
}

function dateKey(c) {
  return `${c.y}-${pad2(c.m)}-${pad2(c.d)}`;
}

function ageOn(y, m, d, today) {
  return today.y - y - (today.m < m || (today.m === m && today.d < d) ? 1 : 0);
}

// Date of birth: month and day are faked from the real date; the year is the
// one that keeps the age the same as the real age, so an age shown beside it
// stays correct (it is the real birth year unless the fake birthday falls on
// the other side of today). Ages 90 and over are not disclosed (Safe Harbor):
// the fake age is 90-99 and a displayed age reads "90+". A future date is not a
// birth date the policy can make coherent, so it is redacted.
function fakeDobParts(c, today = todayParts()) {
  const seed = seedFor('dob', dateKey(c));
  let m = 1 + (seed % 12);
  const d = 1 + ((seed >>> 4) % 28);
  if (m === c.m && d === c.d) m = (m % 12) + 1;
  const realAge = ageOn(c.y, c.m, c.d, today);
  if (realAge < 0) return null;
  const age = realAge >= 90 ? 90 + ((seed >>> 9) % 10) : realAge;
  let y = today.y - age;
  if (today.m < m || (today.m === m && today.d < d)) y -= 1;
  return { y, m, d };
}

const EMBEDDED_DATE_RE = /\b\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}\b|\b\d{4}-\d{2}-\d{2}\b|\b[A-Za-z]{3,9}\.?\s+\d{1,2},?\s+\d{4}\b/g;

function spoofDobText(text) {
  const trimmed = text.trim();
  const c = parseDate(trimmed);
  if (c) {
    const fake = fakeDobParts(c);
    return fake ? formatDate(fake, c.fmt) : null;
  }
  // "DOB 01/15/1945": the label stays, every date in it is faked, and
  // anything else left over means the value is not just a labelled date.
  let ok = true;
  let found = false;
  const out = trimmed.replace(EMBEDDED_DATE_RE, (match) => {
    const parsed = parseDate(match);
    const fake = parsed && fakeDobParts(parsed);
    if (!fake) {
      ok = false;
      return match;
    }
    found = true;
    return formatDate(fake, parsed.fmt);
  });
  if (!ok || !found) return null;
  const residue = trimmed.replace(EMBEDDED_DATE_RE, ' ');
  if (/\d/.test(residue)) return null;
  const words = residue.match(/[A-Za-z]+/g) || [];
  return words.every((w) => LABEL_WORDS.has(w.toLowerCase())) ? out : null;
}

// Dates in untagged-by-kind text keep #2527's behaviour (fully faked, year
// included), now consistent across formats.
function spoofDateCompat(text) {
  const trimmed = text.trim();
  const c = parseDate(trimmed);
  if (!c) return spoofDate(djb2(trimmed));
  const seed = seedFor('date', dateKey(c));
  const fake = { y: 1940 + ((seed >>> 8) % 60), m: 1 + (seed % 12), d: 1 + ((seed >>> 4) % 28) };
  if (fake.y === c.y && fake.m === c.m && fake.d === c.d) fake.d = (fake.d % 28) + 1;
  return formatDate(fake, c.fmt);
}

// Event dates (data-spoof-kind="date": visits, admits, discharges, due
// dates).
//   - A date on or after today (the presenter's local date at render time) is
//     left as it is: due, scheduled and upcoming dates are not identifying on
//     their own, and moving them earlier made future due dates read overdue.
//   - A date before today, inside a record scope (an ancestor with
//     data-demo-record, see demoRecordKey), moves by that record's one fixed
//     offset, always 7-30 days EARLIER: never zero or near-zero, the gaps
//     between a record's past dates stay exact, and a past date stays past.
//     The gap between a past date and a future one on the same record widens
//     by the offset (up to 30 days).
//   - A date before today with no record scope is fully faked, as untyped
//     dates are.
// Numbers computed from dates (days since, days overdue) are not shifted.
export const DATE_SHIFT_MIN_DAYS = 7;
export const DATE_SHIFT_MAX_DAYS = 30;

function dateShiftDays(record) {
  const span = DATE_SHIFT_MAX_DAYS - DATE_SHIFT_MIN_DAYS + 1;
  return -(DATE_SHIFT_MIN_DAYS + (seedFor('date-shift', record) % span));
}

function shiftParts({ y, m, d }, days) {
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}

// Month/day with no year ("9/28", as toLocaleString writes with
// month+day+time options). Arithmetic assumes the current year; the output
// has no year either.
function parseMonthDay(text) {
  const m = text.match(/^(\d{1,2})([/.-])(\d{1,2})$/);
  if (!m) return null;
  const c = validDate({ y: todayParts().y, m: Number(m[1]), d: Number(m[3]) });
  return c && { ...c, fmt: { type: 'md', sep: m[2], padM: m[1].length === 2, padD: m[3].length === 2 } };
}

function isOnOrAfterToday(c) {
  const t = todayParts();
  return c.y > t.y || (c.y === t.y && (c.m > t.m || (c.m === t.m && c.d >= t.d)));
}

// A past date's replacement (see above); null when it cannot be made coherent.
function pastEventDate(c, record, raw) {
  if (record) return formatDate(shiftParts(c, dateShiftDays(record)), c.fmt);
  return c.fmt.type === 'md' ? null : spoofDateCompat(raw);
}

// A trailing time ("9/28, 8:00 AM", "01/15/2026 14:30") is kept as written.
const TRAILING_TIME_RE = /^(.*?)(,?\s+\d{1,2}:\d{2}(?::\d{2})?(?:\s*[AaPp]\.?[Mm]\.?)?)$/;

const EVENT_DATE_WORDS = new Set([
  'admitted', 'admit', 'discharged', 'discharge', 'visit', 'seen', 'due', 'on',
  'completed', 'updated', 'sent', 'last', 'next', 'since', 'until', 'to', 'from',
]);

function spoofEventDateText(text, record) {
  const trimmed = text.trim();
  let datePart = trimmed;
  let time = '';
  const t = trimmed.match(TRAILING_TIME_RE);
  if (t && (parseDate(t[1].trim()) || parseMonthDay(t[1].trim()))) {
    datePart = t[1].trim();
    time = t[2];
  }
  const c = parseDate(datePart) || parseMonthDay(datePart);
  if (c) {
    if (isOnOrAfterToday(c)) {
      passedThrough = true;
      return text;
    }
    const past = pastEventDate(c, record, datePart);
    return past === null ? null : past + time;
  }
  // "Admitted 09/28/2026": label words stay, every date in it moves, and
  // anything else left over means the value is not just a labelled date.
  let ok = true;
  let found = false;
  let shifted = false;
  const out = trimmed.replace(EMBEDDED_DATE_RE, (match) => {
    const parsed = parseDate(match);
    if (!parsed) {
      ok = false;
      return match;
    }
    found = true;
    if (isOnOrAfterToday(parsed)) return match;
    shifted = true;
    return pastEventDate(parsed, record, match);
  });
  if (!ok || !found) return null;
  const residue = trimmed.replace(EMBEDDED_DATE_RE, ' ');
  if (/\d/.test(residue)) return null;
  const words = residue.match(/[A-Za-z]+/g) || [];
  if (!words.every((w) => LABEL_WORDS.has(w.toLowerCase()) || EVENT_DATE_WORDS.has(w.toLowerCase()))) return null;
  // Only future dates and label words: shown as written.
  if (!shifted) passedThrough = true;
  return shifted ? out : text;
}

// Opaque key for a record scope: `:data-demo-record="demoRecordAttr(id)"`.
// A hash of the record's id, never the id itself; the same id gives the same
// key on every screen, after a reload and in another tab.
export function demoRecordKey(id) {
  if (id == null || id === '') return undefined;
  return `r${seedFor('record', String(id)).toString(36)}`;
}

// The attribute value to bind: the key while Demo Mode is on, nothing
// otherwise (ordinary-mode markup is unchanged). Reactive through isDemoMode.
export function demoRecordAttr(id) {
  return isDemoMode() ? demoRecordKey(id) : undefined;
}

// --- Identifiers ---
//
// Format-preserving: a letter becomes a letter (case kept), a digit a digit,
// separators stay. MBI, NPI, TIN and SSN shapes are regenerated so they still
// pass their validators. Label words ("MRN", "ID") are kept. Each token hashes
// on its own, so an identifier reads the same alone and inside a label.
function substituteChars(token, seed) {
  const next = generator(seed);
  let out = '';
  for (const ch of token) {
    if (ch >= '0' && ch <= '9') out += String(next(10));
    else if (ch >= 'A' && ch <= 'Z') out += String.fromCharCode(65 + next(26));
    else if (ch >= 'a' && ch <= 'z') out += String.fromCharCode(97 + next(26));
    else out += ch;
  }
  return out;
}

function spoofIdToken(token) {
  const seed = seedFor('identifier', token);
  if (MBI_RE.test(token)) return spoofMbiText(token, seed);
  if (NPI_RE.test(token)) return spoofNpi(seed, token);
  if (TIN_RE.test(token)) return spoofTin(seed);
  if (SSN_RE.test(token)) return spoofSsn(seed);
  let fake = substituteChars(token, seed);
  for (let i = 1; fake === token && i < 5; i += 1) fake = substituteChars(token, seed + i);
  return fake;
}

function spoofIdentifier(text) {
  const trimmed = text.trim();
  let changed = false;
  const out = trimmed.replace(/[^\s:#]+/g, (token) => {
    if (!/[A-Za-z0-9]/.test(token)) return token;
    if (LABEL_WORDS.has(token.toLowerCase().replace(/[.]+$/, ''))) return token;
    changed = true;
    return spoofIdToken(token);
  });
  return changed && out !== trimmed ? out : null;
}

// --- Names ---
//
// One real person is one fake identity: the fake first name is a function of
// the real first name alone, the fake surname of the real surname alone. A
// full-name cell, separate first/last columns, "Dr. Surname" and prose
// mentions are all projections of the same mapping.
const FEMALE_FIRST = new Set(FEMALE_FIRST_NAMES.split(' '));
const MALE_FIRST = new Set(MALE_FIRST_NAMES.split(' '));

// 'female' | 'male' | null (ambiguous or unknown). A hyphenated name with no
// entry of its own takes its first part ("Mary-Ann").
export function firstNameSex(token) {
  const key = String(token).toLowerCase();
  for (const k of [key, key.split(/[-'’]/)[0]]) {
    if (FEMALE_FIRST.has(k)) return 'female';
    if (MALE_FIRST.has(k)) return 'male';
  }
  return null;
}

export const FAKE_LAST_NAMES = Object.freeze([...LAST_NAMES]);

export const FAKE_FIRST_POOLS = Object.freeze({
  female: FEMALE_FAKE_FIRST,
  male: MALE_FAKE_FIRST,
  unisex: UNISEX_FAKE_FIRST,
});

function fakeFirst(token) {
  const pool = FAKE_FIRST_POOLS[firstNameSex(token) || 'unisex'];
  return matchCase(token, pickOther(pool, seedFor('first', token), token));
}

function fakeLast(token) {
  return matchCase(token, pickOther(LAST_NAMES, seedFor('last', token), token));
}

// Learned name tokens, used only to substitute names inside free text. The
// structured fake never depends on this table; it only records which tokens
// have been seen in which role.
const nameTokenRoles = new Map(); // lowercase token -> Set<'first' | 'last'>
let nameTokenRegex = null;
let nameTokenDirty = false;
let nameMapVersion = 0;
let nameMapChangedAt = 0;

function learnToken(token, role) {
  if (!token || token.length < 2) return;
  const key = token.toLowerCase();
  let roles = nameTokenRoles.get(key);
  if (!roles) {
    roles = new Set();
    nameTokenRoles.set(key, roles);
    nameTokenDirty = true;
  }
  if (!roles.has(role)) {
    roles.add(role);
    nameMapVersion++;
    nameMapChangedAt = Date.now();
    notifyNameMapChange();
  }
}

// Prose rewrites (DemoText) are checked against, and keyed on, the learned
// names; they re-check when the map changes. Listeners run once per
// microtask however many tokens were learned.
const nameMapListeners = new Set();
let nameMapNotifyQueued = false;

function notifyNameMapChange() {
  if (nameMapNotifyQueued || !nameMapListeners.size) return;
  nameMapNotifyQueued = true;
  queueMicrotask(() => {
    nameMapNotifyQueued = false;
    for (const fn of [...nameMapListeners]) {
      try {
        fn();
      } catch {
        /* one listener must not stop the others */
      }
    }
  });
}

export function demoNameMapChangedAt() {
  return nameMapChangedAt;
}

export function onDemoNameMapChange(fn) {
  nameMapListeners.add(fn);
  return () => nameMapListeners.delete(fn);
}

function fakeForToken(token, role) {
  learnToken(token, role);
  return role === 'first' ? fakeFirst(token) : fakeLast(token);
}

// Free text has no role. A token seen in both roles uses its surname mapping
// ("Jordan" from "Dr. Jordan" and from "Jordan Smith"), so prose mentioning
// that first name reads as the surname fake: a documented trade-off.
function proseFakeFor(token) {
  const roles = nameTokenRoles.get(token.toLowerCase());
  if (!roles) return null;
  return roles.has('last') ? fakeLast(token) : fakeFirst(token);
}

function rebuildNameRegex() {
  nameTokenDirty = false;
  if (nameTokenRoles.size === 0) {
    nameTokenRegex = null;
    return;
  }
  const keys = [...nameTokenRoles.keys()]
    .sort((a, b) => b.length - a.length)
    .map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  nameTokenRegex = new RegExp('\\b(' + keys.join('|') + ')\\b', 'gi');
}

function applyNameTokens(text) {
  if (nameTokenDirty) rebuildNameRegex();
  if (!nameTokenRegex) return text;
  return text.replace(nameTokenRegex, (m) => {
    // Only substitute capitalized occurrences -- a lowercase match (e.g. "young",
    // "gray", "cook") is almost always a common word, not the person's name.
    if (m[0] === m[0].toLowerCase()) return m;
    return proseFakeFor(m) ?? m;
  });
}

const NAME_WORD_RE = /^[A-Za-z][A-Za-z'’-]*$/;
const INITIAL_RE = /^[A-Za-z]\.?$/;
const PARTICLES = new Set(['de', 'del', 'della', 'der', 'da', 'di', 'du', 'la', 'le', 'van', 'von', 'den', 'bin', 'ibn', 'al']);
const NAME_SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv']);
const CREDENTIALS = new Set([
  'md', 'do', 'np', 'pa', 'pa-c', 'rn', 'aprn', 'fnp', 'fnp-c', 'fnp-bc', 'dnp',
  'anp', 'agnp', 'agnp-c', 'acnp', 'cnm', 'crna', 'cns', 'phd', 'mbbs', 'dpm',
  'dc', 'od', 'pharmd', 'lcsw', 'msw', 'lpn', 'lvn', 'cna', 'mph', 'mba', 'ms',
  'msn', 'bsn', 'dds', 'dmd', 'pt', 'dpt', 'ot', 'otr', 'facp', 'faafp', 'cma',
  'ccm', 'chw',
]);
const HONORIFIC_RE = /^((?:Dr|Doctor|Prof|Mr|Mrs|Ms|Mx)\.?\s+)(\S.*)$/i;

// A trailing credential is written in capitals ("MD", "PA-C") or with an
// inner capital ("PhD", "PharmD"); "Do" in title case is a given name.
function isNameAffix(token) {
  const bare = token.replace(/\.$/, '');
  const lower = bare.toLowerCase();
  if (NAME_SUFFIXES.has(lower)) return true;
  if (!CREDENTIALS.has(lower)) return false;
  return bare === bare.toUpperCase() || /^.[a-z]*[A-Z]/.test(bare);
}

function fakeInitial(initial, context) {
  const letter = String.fromCharCode(65 + (seedFor('initial', `${initial}|${context}`) % 26));
  const fake = initial[0] === initial[0].toLowerCase() ? letter.toLowerCase() : letter;
  return fake + initial.slice(1);
}

// Spoof a person's name, keeping its order, punctuation, honorific and
// credentials. mode: 'full' (either order; a lone word is a surname),
// 'first' or 'last' (every name word takes that role). Returns null when the
// text is not a name shape the mapping can make coherent.
function spoofPersonName(text, mode = 'full') {
  let core = text.trim();
  let head = '';
  let tail = '';
  const hon = core.match(HONORIFIC_RE);
  if (hon) {
    head = hon[1];
    core = hon[2];
  }
  for (;;) {
    const m = core.match(/^(.*?[^\s,])(,?\s+)([A-Za-z]{1,7}(?:-[A-Za-z]{1,2})?\.?)$/);
    if (!m || !isNameAffix(m[3])) break;
    tail = m[2] + m[3] + tail;
    core = m[1];
  }
  const words = [...core.matchAll(/[^\s,]+/g)];
  if (!words.length || words.length > 5) return null;
  const commaAt = core.indexOf(',');
  if (commaAt !== core.lastIndexOf(',')) return null;

  const roles = words.map((w, i) => {
    const token = w[0];
    if (PARTICLES.has(token) && words.length > 1) return 'keep';
    if (INITIAL_RE.test(token)) return 'initial';
    if (!NAME_WORD_RE.test(token)) return null;
    if (mode === 'first' || mode === 'last') return mode;
    if (commaAt >= 0) return w.index < commaAt ? 'last' : 'first';
    return i === words.length - 1 ? 'last' : 'first';
  });
  if (roles.includes(null)) return null;
  if (!roles.some((r) => r === 'first' || r === 'last')) return null;

  const surname = words.filter((_, i) => roles[i] === 'last').map((w) => w[0]).join(' ');
  let out = '';
  let at = 0;
  words.forEach((w, i) => {
    out += core.slice(at, w.index);
    const token = w[0];
    const role = roles[i];
    if (role === 'first' || role === 'last') out += fakeForToken(token, role);
    else if (role === 'initial') out += fakeInitial(token, surname);
    else out += token;
    at = w.index + token.length;
  });
  out += core.slice(at);
  return head + out + tail;
}

// --- Pattern detection ---

const PHONE_RE = /^[\s(]*\d{3}[\s).-]+\d{3}[\s.-]+\d{4}\s*$/;
const MBI_RE = /^[0-9][A-Z][A-Z0-9]{2}-?[A-Z0-9]{3}-?[A-Z0-9]{4}$/i;
const NPI_RE = /^[12]\d{9}$/;
const TIN_RE = /^\d{2}-\d{7}$/;
const SSN_RE = /^\d{3}-\d{2}-\d{4}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE_RE = /^(\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}|\w{3}\s+\d{1,2},?\s+\d{4}|\d{4}-\d{2}-\d{2})$/;
const ZIP_RE = /^\d{5}(-\d{4})?$/;
const ADDRESS_RE = /^\d+\s+\w+.*(st|ave|dr|ln|blvd|ct|rd|way|street|avenue|drive|lane|boulevard|court|road|circle)\b/i;
// A single token with both letters and digits, or a long digit run: an
// identifier in a format the specific patterns above do not know.
const IDENTIFIER_TOKEN_RE = /^(?:(?=[A-Za-z0-9-]*\d)(?=[A-Za-z0-9-]*[A-Za-z])[A-Za-z0-9][A-Za-z0-9-]{2,23}|\d{6,24})$/;

// Every identifier format the app recognizes, so tests can prove a route
// stand-in token never passes for a real identifier.
export const IDENTIFIER_FORMATS = Object.freeze({
  mbi: MBI_RE,
  npi: NPI_RE,
  tin: TIN_RE,
  ssn: SSN_RE,
  phone: PHONE_RE,
  zip: ZIP_RE,
  // Any other identifier shape (member ids, MRNs, synthetic ids).
  other: IDENTIFIER_TOKEN_RE,
});

// Embedded patterns for compound text like "DOB: 01/15/1945" or "MBI 1A23B45C678".
// Replace the sensitive value in-place so the label is preserved and nothing leaks.
// The identity patterns (everything but dates) also drive the prose rewrite
// fakes map and its leak check (demoFakesFor, demoRewriteIdentityHits).
const IDENTITY_EMBEDDED_RES = [
  {
    re: /[^\s@]+@[^\s@]+\.[^\s@]+/g,
    fn: (m) => spoofEmail(seedFor('email', m)),
    norm: (m) => m.replace(/[.,;:!?)\]]+$/, '').toLowerCase(),
    // Prose puts punctuation after an address; the fakes map keys the address.
    clean: (m) => m.replace(/[.,;:!?)\]]+$/, ''),
  },
  { re: /\(?\d{3}\)?[\s.-]+\d{3}[\s.-]+\d{4}/g, fn: spoofPhoneText, norm: digitsOf },
  { re: /\b\d{3}-\d{2}-\d{4}\b/g, fn: spoofIdToken, norm: digitsOf },
  { re: /\b\d{2}-\d{7}\b/g, fn: spoofIdToken, norm: digitsOf },
  { re: /\b[12]\d{9}\b/g, fn: spoofIdToken, norm: digitsOf },
  { re: /\b[0-9][A-Z][A-Z0-9]{2}-?[A-Z0-9]{3}-?[A-Z0-9]{4}\b/gi, fn: spoofIdToken, norm: (m) => m.replace(/-/g, '').toUpperCase() },
];
const EMBEDDED_RES = [
  ...IDENTITY_EMBEDDED_RES,
  { re: /\b\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}\b/g, fn: spoofDateCompat },
  { re: /\b\d{4}-\d{2}-\d{2}\b/g, fn: spoofDateCompat },
  { re: /\b\w{3}\s+\d{1,2},?\s+\d{4}\b/g, fn: spoofDateCompat },
];

// Apply embedded value scrubbing (dates/phones/MBI/email) AND learned name-token
// substitution to arbitrary free text. Used for compound labels and untagged
// attributes. A changed result is NOT evidence that the text is safe: anything
// the patterns do not recognize survives, so tagged callers must pair this with
// residueIsLabelOnly() before trusting it.
function transformFreeText(text) {
  let result = text;
  for (const { re, fn } of EMBEDDED_RES) {
    result = result.replace(re, (m) => fn(m));
  }
  result = applyNameTokens(result);
  return applyBrand(result);
}

// Words that may legitimately surround an embedded value in a compound label
// ("DOB: 01/15/1945", "Phone (home) 555-...", "Dr. Chen"). Anything else left
// over after removing recognized values is unknown content.
const LABEL_WORDS = new Set([
  'dob', 'date', 'of', 'birth', 'born', 'mbi', 'npi', 'tin', 'ssn', 'mrn', 'id',
  'phone', 'tel', 'mobile', 'cell', 'home', 'work', 'fax', 'email', 'e', 'mail',
  'member', 'patient', 'name', 'age', 'zip', 'dr', 'mr', 'mrs', 'ms', 'and', 'or',
]);

// True when every word left after removing recognized PHI values and learned,
// capitalized name tokens is a known label word -- i.e. a compound transform
// replaced everything identifying rather than just one piece of it.
function residueIsLabelOnly(text) {
  let residue = text;
  for (const { re } of EMBEDDED_RES) residue = residue.replace(re, ' ');
  if (nameTokenDirty) rebuildNameRegex();
  if (nameTokenRegex) {
    residue = residue.replace(nameTokenRegex, (m) =>
      m[0] === m[0].toLowerCase() ? m : ' '
    );
  }
  // Leftover digits are an unrecognized identifier (an MRN, account or member
  // number), never a label.
  if (/\d/.test(residue)) return false;
  // Any script: a name in Cyrillic, Greek, Han etc. is not a label word, and
  // an ASCII-only match would skip it entirely and pass the residue as safe.
  const words = residue.match(/[\p{L}\p{M}]+/gu) || [];
  return words.every((w) => LABEL_WORDS.has(w.toLowerCase()));
}

// Person-name shapes we trust enough to LEARN tokens from (so the same name is
// spoofed consistently wherever else it appears, including free text).
// Allow internal apostrophes/hyphens and all-caps so "McDonald", "O'Brien",
// "JAMES CHEN" are recognized (and learned) rather than partially leaked.
const LAST_FIRST_RE = /^([A-Z][A-Za-z'’-]+),\s*([A-Z][A-Za-z'’-]+)\b/;
const TITLE_SURNAME_RE = /^((?:Dr|Mr|Mrs|Ms|Doctor|Prof)\.?)\s+([A-Z][A-Za-z'’-]+)$/;
const FIRST_LAST_RE = /^([A-Z][A-Za-z'’-]+)\s+([A-Z][A-Za-z'’-]+)$/;
// "Call Zelda Quince", "Follow up with Dr. Jordan": a closed set of task
// words in front of a strict name shape. The words stay; the name is spoofed.
const TASK_PREFIX_RE = /^((?:(?:call|email|text|visit|contact|schedule|review|remind|message|follow|up|with|outreach|to|for|meet|see|check|in|on|reach|out)\s+)+)(\S.*)$/i;

function isPlaceholderValue(trimmed) {
  return !trimmed || trimmed === '—' || trimmed === '-' || trimmed === 'N/A';
}

// Values that are not identifying on their own and read wrong when faked: an
// age or count with its unit, a plain number, and a small closed set of
// categories. They pass through unchanged, except that an age of 90 or more
// reads "90+" (Safe Harbor).
const SAFE_CATEGORY_WORDS = new Set([
  'male', 'female', 'm', 'f', 'unknown', 'other', 'non-binary', 'nonbinary',
  'active', 'inactive', 'yes', 'no', 'y', 'n', 'true', 'false',
  // Contact relationships
  'self', 'spouse', 'husband', 'wife', 'partner', 'daughter', 'son', 'child',
  'mother', 'father', 'parent', 'sister', 'brother', 'sibling', 'friend',
  'caregiver', 'guardian', 'relative', 'grandchild', 'grandparent', 'neighbor',
]);
const AGE_RE = /^(\d{1,3})(\s*)(yrs?|years?|y\/o|yo)(\.?)(\s+old)?$/i;
const COUNT_UNITS = new Set([
  'mos', 'mo', 'month', 'months', 'day', 'days', 'wk', 'wks', 'week', 'weeks',
  'hr', 'hrs', 'hour', 'hours', 'min', 'mins', 'minutes', 'visit', 'visits',
  'claim', 'claims', 'admit', 'admits', 'admissions', 'times', 'x', 'mg', 'ml',
  'kg', 'lb', 'lbs', 'pts', 'points', 'gaps', 'encounters', 'calls', 'tasks',
  'items', 'meds', 'medications', 'conditions', 'hccs',
]);
const PLAIN_NUMBER_RE = /^[-+]?\$?(?:\d{1,3}(?:,\d{3})+|\d{1,4})(?:\.\d+)?%?$/;
const COUNT_RE = /^[-+]?\d{1,4}(?:[.,]\d+)?\s+([A-Za-z]+)$/;

function passthroughValue(trimmed) {
  if (SAFE_CATEGORY_WORDS.has(trimmed.toLowerCase())) return trimmed;
  if (PLAIN_NUMBER_RE.test(trimmed)) return trimmed;
  const age = trimmed.match(AGE_RE);
  if (age) {
    if (Number(age[1]) < 90) return trimmed;
    return `90+${age[2] || ' '}${age[3]}${age[4]}${age[5] || ''}`;
  }
  const count = trimmed.match(COUNT_RE);
  if (count && COUNT_UNITS.has(count[1].toLowerCase())) return trimmed;
  return null;
}

// --- Typed spoof kinds ---
//
// An element (or ag-grid column, via a `spoof-kind-<kind>` class) may declare
// what its text is with data-spoof-kind. Typed values skip the heuristic
// ladder entirely; a value that does not fit its kind is redacted.
export const SPOOF_KINDS = Object.freeze([
  'person', 'person-first', 'person-last', 'provider',
  'practice', 'facility', 'payer', 'contract', 'tenant',
  'identifier', 'dob', 'date', 'phone', 'email', 'address', 'zip',
]);
const SPOOF_KIND_SET = new Set(SPOOF_KINDS);
const KIND_CLASS_PREFIX = 'spoof-kind-';
const RECORD_CLASS_PREFIX = 'demo-record-';
const ORG_POOLS = {
  practice: PRACTICE_NAMES,
  facility: FACILITY_NAMES,
  payer: PAYER_NAMES,
  contract: CONTRACT_NAMES,
};

// ag-grid column helper: `cellClass: sensitiveCell('person-last')`. An event
// date column passes the row's record id so its dates share the record's
// offset: `cellClass: (p) => sensitiveCell('date', p.data?.patient_identifier)`.
// The record class, like demoRecordAttr, is only added while Demo Mode is on.
export function sensitiveCell(kind, recordId) {
  if (kind && !SPOOF_KIND_SET.has(kind)) throw new Error(`Unknown spoof kind: ${kind}`);
  const record = isDemoMode() ? demoRecordKey(recordId) : undefined;
  return [
    'sensitive',
    kind ? `${KIND_CLASS_PREFIX}${kind}` : null,
    record ? `${RECORD_CLASS_PREFIX}${record}` : null,
  ].filter(Boolean).join(' ');
}

// Tenant names map to the fake tenant pool, or to the neutral brand when they
// carry it.
function spoofTenantName(text) {
  const trimmed = text.trim();
  if (!trimmed) return text;
  const branded = applyBrand(trimmed);
  return branded !== trimmed ? branded : pickOther(TENANT_NAMES, seedFor('tenant', trimmed), trimmed);
}

// Organization values the engine has typed and faked on this page, real ->
// fake. Prose rewrites are sent the pairs whose real form their text holds,
// so a headline names a practice the way the title above it does.
const ORG_FAKE_KINDS = new Set(['practice', 'facility', 'payer', 'contract', 'tenant']);
const orgFakes = new Map();

function recordOrgFake(real, fake) {
  if (!real || !fake || fake === real || orgFakes.get(real) === fake) return;
  orgFakes.set(real, fake);
  nameMapVersion++;
  nameMapChangedAt = Date.now();
  notifyNameMapChange();
}

function spoofTyped(kind, text, record = null) {
  const trimmed = text.trim();
  if (isPlaceholderValue(trimmed)) return text;
  const out = spoofTypedValue(kind, trimmed, text, record);
  if (ORG_FAKE_KINDS.has(kind) && typeof out === 'string') recordOrgFake(trimmed, out.trim());
  return out;
}

function spoofTypedValue(kind, trimmed, text, record) {
  switch (kind) {
    case 'person':
    case 'provider':
      return spoofPersonName(trimmed, 'full');
    case 'person-first':
      return spoofPersonName(trimmed, 'first');
    case 'person-last':
      return spoofPersonName(trimmed, 'last');
    case 'practice':
    case 'facility':
    case 'payer':
    case 'contract':
      return pickOther(ORG_POOLS[kind], seedFor(kind, trimmed), trimmed);
    case 'tenant':
      return spoofTenantName(trimmed);
    case 'identifier':
      return spoofIdentifier(trimmed);
    case 'dob':
      return spoofDobText(trimmed);
    case 'date':
      return spoofEventDateText(trimmed, record);
    case 'phone':
      return spoofPhoneText(trimmed);
    case 'email':
      return spoofEmail(seedFor('email', trimmed));
    case 'address':
      return spoofAddressText(trimmed);
    case 'zip':
      return spoofZipText(trimmed);
    default:
      // Unknown kind: nothing proves the value is masked.
      return null;
  }
}

// Aggressive detector for tagged values. Returns a replacement string, or null
// when the value cannot be proven fully masked -- the caller must then redact.
function detectAndSpoof(text, kind = null, record = null) {
  return classifyAndSpoof(text, kind, record).value;
}

// `stable` is true when the result cannot change as more name tokens are
// learned. Typed kinds and pattern/shape matches are pure functions of the
// value; only the learned-token branch is order-dependent, so only those
// elements need a rescan when the map grows. `passthrough` marks a value
// deliberately shown as-is (an age, a sex), which is not a failed spoof.
let usedLearnedState = false;
let passedThrough = false;

function classifyAndSpoof(text, kind = null, record = null) {
  usedLearnedState = false;
  passedThrough = false;
  const value = kind ? spoofTyped(kind, text, record) : detectAndSpoofInner(text);
  return { value, stable: !usedLearnedState, passthrough: passedThrough };
}

function detectAndSpoofInner(text) {
  const trimmed = text.trim();
  if (isPlaceholderValue(trimmed)) return text;

  const safe = passthroughValue(trimmed);
  if (safe !== null) {
    passedThrough = safe === trimmed;
    return passedThrough ? text : safe;
  }

  if (PHONE_RE.test(trimmed)) return spoofPhoneText(trimmed);
  if (MBI_RE.test(trimmed) || NPI_RE.test(trimmed) || TIN_RE.test(trimmed) || SSN_RE.test(trimmed)) {
    return spoofIdentifier(trimmed);
  }
  if (EMAIL_RE.test(trimmed)) return spoofEmail(seedFor('email', trimmed));
  if (ZIP_RE.test(trimmed)) return spoofZipText(trimmed);
  if (DATE_RE.test(trimmed)) return spoofDateCompat(trimmed);
  if (ADDRESS_RE.test(trimmed)) return spoofAddress(seedFor('address', trimmed));

  // "Last, First" -- learn both tokens for consistent reuse elsewhere
  let m = trimmed.match(LAST_FIRST_RE);
  if (m) return spoofPersonName(trimmed) ?? `${fakeForToken(m[1], 'last')}, ${fakeForToken(m[2], 'first')}`;

  // "Dr. Surname" / "Mr. Surname" -- learn the surname
  if (TITLE_SURNAME_RE.test(trimmed)) return spoofPersonName(trimmed);

  if (trimmed.length <= 60) {
    // Organization keywords pick an organization pool, never a person.
    if (/\b(pharmacy|rx|drug)\b/i.test(trimmed)) {
      return pickOther(PHARMACY_NAMES, seedFor('pharmacy', trimmed), trimmed);
    }
    if (/\b(hospital|regional|medical center)\b/i.test(trimmed)) {
      return pickOther(FACILITY_NAMES, seedFor('facility', trimmed), trimmed);
    }
    if (/\b(medical|health|clinic|care|practice|group|center|associates|partners|family|primary)\b/i.test(trimmed)) {
      return pickOther(PRACTICE_NAMES, seedFor('practice', trimmed), trimmed);
    }
    if (/\b(ACO|MSSP|contract|managed|risk|track)\b/i.test(trimmed)) {
      return pickOther(CONTRACT_NAMES, seedFor('contract', trimmed), trimmed);
    }
    if (/\b(aetna|cigna|humana|united|anthem|blue|shield|molina|centene|wellcare|medicare|medicaid)\b/i.test(trimmed)) {
      return pickOther(PAYER_NAMES, seedFor('payer', trimmed), trimmed);
    }
    if (/\b(network|alliance|collaborative|systems|vytalize|privia|agilon|aledade|oak\s*street|chenmed)\b/i.test(trimmed)) {
      return pickOther(TENANT_NAMES, seedFor('tenant', trimmed), trimmed);
    }
    // "First Last" -- learn BOTH tokens. Must run before the generic free-text
    // transform: otherwise, once one token (e.g. a surname) is already known, the
    // transform would replace just that token and return early, leaking the other
    // (e.g. "Dominick Basile" -> "Dominick <fake>" with the first name intact).
    if (FIRST_LAST_RE.test(trimmed)) return spoofPersonName(trimmed);
    const task = trimmed.match(TASK_PREFIX_RE);
    if (task) {
      const name = task[2];
      if (FIRST_LAST_RE.test(name) || TITLE_SURNAME_RE.test(name) || /^[A-Z][A-Za-z'’-]+,\s*[A-Z][A-Za-z'’-]+$/.test(name)) {
        const spoofed = spoofPersonName(name);
        if (spoofed) return task[1] + spoofed;
      }
    }
    // An identifier in a format nothing above knows ("SYN0001") keeps its
    // shape instead of becoming a name.
    if (IDENTIFIER_TOKEN_RE.test(trimmed)) return spoofIdentifier(trimmed);
    usedLearnedState = true;
    // Compound/embedded values ("DOB: 01/15/1945") -- scrub in place, but only
    // when nothing but label words survives. "Seen by Zelda at Eastport" with
    // just "Zelda" learned must not come back with "Eastport" still readable.
    const transformed = transformFreeText(trimmed);
    if (transformed !== trimmed) {
      // Something was recognized, so this is structured text; if anything
      // else survives (words or digits), redact rather than half-substitute
      // or invent a name ("DOB 01/15/1945 MRN 00012345").
      return residueIsLabelOnly(trimmed) ? transformed : null;
    }
    // A lone capitalized word in an untyped tag has no role; it takes the
    // surname mapping (the same one prose and person-last use) without being
    // learned. Typed columns (person-first) get the role-correct pool.
    if (NAME_WORD_RE.test(trimmed) && /^[A-Z]/.test(trimmed)) return fakeLast(trimmed);
    // Anything else fails every recognizer: redact rather than invent a
    // person (a wrong fake is worse than a redaction).
    return null;
  }

  // Long tagged text is prose. A partial substitution (one date or one learned
  // name) leaves nicknames, relatives and places readable, so it is redacted.
  return null;
}

// Conservative detector for document-wide attribute scrubbing. Unlike
// detectAndSpoof (which assumes its input IS sensitive), this only transforms
// when a concrete PHI pattern, a confident name shape, or an already-learned
// name token is present. Generic UI chrome ("Sort ascending", "Close",
// "Filter") is returned UNCHANGED so we don't turn every tooltip in the app
// into a fake patient name. Never returns null -- when nothing matches, the
// original is preserved.
function spoofKnownPhi(text) {
  const trimmed = text.trim();
  if (!trimmed) return text;

  if (PHONE_RE.test(trimmed)) return spoofPhoneText(trimmed);
  if (MBI_RE.test(trimmed) || NPI_RE.test(trimmed) || TIN_RE.test(trimmed) || SSN_RE.test(trimmed)) {
    return spoofIdentifier(trimmed) ?? text;
  }
  if (EMAIL_RE.test(trimmed)) return spoofEmail(seedFor('email', trimmed));
  if (ZIP_RE.test(trimmed)) return spoofZipText(trimmed);
  if (DATE_RE.test(trimmed)) return spoofDateCompat(trimmed);
  if (ADDRESS_RE.test(trimmed)) return spoofAddress(seedFor('address', trimmed));

  // Only the comma form ("Smith, John") and titled form ("Dr. Smith") are
  // name-specific enough to spoof on sight document-wide. We deliberately do NOT
  // run the bare "First Last" shape here: ordinary two-word UI chrome ("Sort
  // Ascending", "Export Data", "Pin Column") matches it and would be turned into
  // fake names. A genuine bare first-last name still gets caught below via the
  // learned-token path once those tokens are seen in a .sensitive field.
  const m = trimmed.match(LAST_FIRST_RE);
  if (m) return spoofPersonName(trimmed) ?? `${fakeForToken(m[1], 'last')}, ${fakeForToken(m[2], 'first')}`;
  if (TITLE_SURNAME_RE.test(trimmed)) return spoofPersonName(trimmed) ?? text;

  // Embedded PHI ("Claim Date: ... | NPI: 1234567890 | TIN: 12-3456789") or an
  // already-learned name appearing inside otherwise-generic text.
  const t = transformFreeText(trimmed);
  return t !== trimmed ? t : text;
}

// Deterministic spoof of a single free-text string for non-DOM consumers (chart
// data, canvas labels). Shares the same mappings as the DOM engine so a real
// name maps to the SAME fake everywhere. `kind` is one of SPOOF_KINDS when
// the caller knows what the value is; otherwise the heuristic ladder runs.
// Text that cannot be masked completely comes back empty rather than
// partially transformed.
export function spoofText(str, kind = null, { record = null } = {}) {
  if (str == null) return str;
  const text = String(str);
  if (!text.trim()) return text;
  try {
    const out = detectAndSpoof(text, kind, demoRecordKey(record) ?? null);
    return out === null ? '' : applyBrand(out);
  } catch {
    return '';
  }
}

// --- Prose rewrites ---
//
// Long prose cannot be spoofed in place, so DemoText asks the server for a
// rewrite of it (utils/demoRewrite). These helpers are the browser's side of
// that contract; they reuse the mappings above rather than adding new ones.

// A run of 7+ digits immediately followed (whitespace allowed) by one of these
// units is a lab value, not an identifier: "12345678 copies/mL".
export const CLINICAL_UNIT_RE = /^\s*(copies\/mL|cells|mg)(?![\p{L}\p{N}/_])/iu;
export const LONG_DIGIT_RUN_RE = /(?<!\d)\d{7,}(?!\d)/g;

// A bare digit run directly followed by a lab unit is a lab value
// ("1234567890 copies/mL"), not an identifier, whatever pattern it fits. The
// long-number check still rejects it if a rewrite drops or changes the unit.
function isLabValueAt(text, match, index) {
  return /^\d+$/.test(match) && CLINICAL_UNIT_RE.test(text.slice(index + match.length));
}

function forEachMatch(text, re, fn) {
  for (const m of text.matchAll(re)) fn(m[0], m.index);
}

function learnedNameSpans(text) {
  if (nameTokenDirty) rebuildNameRegex();
  const out = [];
  if (!nameTokenRegex) return out;
  forEachMatch(text, nameTokenRegex, (m) => {
    if (m[0] !== m[0].toLowerCase()) out.push(m);
  });
  return out;
}

// real -> fake pairs the server should use for these texts: learned name
// tokens as they are spelled in the text (so casing is kept) through the
// same prose mapping the DOM engine uses, recognizable identifiers through
// their deterministic typed fakes, long digit runs that are not lab values,
// the customer brand, and the page's typed organization fakes. Nothing is
// stored.
export function demoFakesFor(texts) {
  const fakes = {};
  for (const raw of texts || []) {
    if (!raw) continue;
    const text = String(raw);
    for (const span of learnedNameSpans(text)) {
      const fake = proseFakeFor(span);
      if (fake) fakes[span] = fake;
    }
    for (const [real, fake] of orgFakes) {
      const re = new RegExp(`(?<![\\p{L}\\p{N}])${real.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\p{L}\\p{N}])`, 'giu');
      forEachMatch(text, re, (m) => {
        fakes[m] = fake;
      });
    }
    for (const { re, fn, clean } of IDENTITY_EMBEDDED_RES) {
      forEachMatch(text, re, (m, index) => {
        if (isLabValueAt(text, m, index)) return;
        const key = clean ? clean(m) : m;
        fakes[key] = fn(key);
      });
    }
    forEachMatch(text, LONG_DIGIT_RUN_RE, (m, index) => {
      if (fakes[m] || isLabValueAt(text, m, index)) return;
      fakes[m] = spoofIdToken(m);
    });
    forEachMatch(text, BRAND_RE, (m) => {
      fakes[m] = applyBrand(m);
    });
  }
  return fakes;
}

// How many identity values from the original survive in a rewrite: a learned
// name (capitalized, as prose substitution matches it), a real key of the
// fakes map, or an identifier the original holds in any formatting. Fake
// values the server was given are allowed even when someone else carries the
// same name, unless that value is also a real key of this map (a swap the
// check cannot tell apart). Counts only; never the values.
export function demoRewriteIdentityHits(original, rewrite, fakes = {}) {
  const text = String(rewrite ?? '');
  const lowerText = text.toLowerCase();
  const realKeys = new Set(Object.keys(fakes).map((k) => k.toLowerCase()));
  const fakeValues = new Set(Object.values(fakes).map((v) => String(v).toLowerCase()));
  let hits = 0;
  for (const span of learnedNameSpans(text)) {
    const lower = span.toLowerCase();
    if (fakeValues.has(lower) && !realKeys.has(lower)) continue;
    hits += 1;
  }
  for (const key of realKeys) {
    if (!key) continue;
    if (/^[\p{L}'’-]+$/u.test(key)) {
      // A name word: whole words, and like prose substitution, not when the
      // occurrence is all lower case ("young", "gray" are ordinary words).
      const re = new RegExp(`(?<![\\p{L}\\p{N}])${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\p{L}\\p{N}])`, 'giu');
      for (const m of text.matchAll(re)) {
        if (m[0] !== m[0].toLowerCase()) hits += 1;
      }
    } else if (lowerText.includes(key)) {
      hits += 1;
    }
  }
  const source = String(original ?? '');
  for (const { re, norm } of IDENTITY_EMBEDDED_RES) {
    const known = new Set();
    forEachMatch(source, re, (m, index) => {
      if (!isLabValueAt(source, m, index)) known.add(norm(m));
    });
    if (!known.size) continue;
    forEachMatch(text, re, (m) => {
      if (known.has(norm(m))) hits += 1;
    });
  }
  return hits;
}

// The customer brand mask, for rewritten prose shown inside a tagged element
// (where the document-wide brand pass does not reach).
export function demoBrandText(text) {
  return applyBrand(text);
}

// A value shown where no tagged element can hold it on its own: an <option>
// label that mixes it with other text ("A1234 - 01/2026 (52 patients)").
// The value itself while Demo Mode is off, its typed fake while it is on.
// Reactive through isDemoMode, so a template re-renders on toggle.
export function demoLabel(value, kind) {
  if (value == null || value === '') return value;
  return isDemoMode() ? spoofText(value, kind) : value;
}

// ag-grid set filters draw their value list, mini-filter matches and the
// floating-filter summary from raw values, outside the tagged cells. Spread
// into the filterParams of a sensitive column; keys and filtering stay real.
// The list is re-formatted whenever it opens, but a floating-filter summary is
// only rebuilt with the header, so grids that used the formatter get their
// header refreshed when Demo Mode changes.
const setFilterGrids = new Set(); // WeakRef<GridApi>
const setFilterGridSeen = new WeakSet();
let setFilterWatchStop = null;

function refreshSetFilterGrids() {
  for (const ref of [...setFilterGrids]) {
    const api = ref.deref();
    if (!api || api.isDestroyed?.()) {
      setFilterGrids.delete(ref);
      continue;
    }
    try {
      api.refreshHeader();
    } catch {
      /* a grid mid-teardown */
    }
  }
}

function trackSetFilterGrid(api) {
  if (!api || setFilterGridSeen.has(api)) return;
  setFilterGridSeen.add(api);
  setFilterGrids.add(new WeakRef(api));
  if (!setFilterWatchStop) {
    // Detached from whatever component was rendering when the first set
    // filter formatted a value, so it outlives that component.
    setFilterWatchStop = effectScope(true).run(() =>
      store.watch((state, getters) => getters['demoMode/isDemoMode'], refreshSetFilterGrids)
    );
  }
}

export const sensitiveSetFilterParams = Object.freeze({
  valueFormatter(params) {
    trackSetFilterGrid(params.api);
    if (isDemoMode()) return spoofText(params.value);
    // ag-grid shows a non-string label (a number) as an empty row.
    return params.value == null ? null : String(params.value);
  },
});

// --- DOM processing ---
//
// Every processed element keeps an entry in `originals`:
//   { mode: 'spoof' | 'redact', nodes: [{ node, original, written }],
//     attrs: { [name]: { original, written } }, controls: Map<control, saved> }
//
// Provenance is PER TEXT NODE: `written` is exactly what the engine put in that
// node. On every pass a node whose current value still equals `written` is
// reinterpreted as its stored `original`; any other value is new application
// content. The replacement is then recomputed from originals, so the engine
// never spoofs its own output (spoof(spoof(x)) cycles endlessly) and a stamp is
// never an exemption for content the engine has not seen. An earlier design
// used one global set of emitted values; a real value could collide with some
// other field's fake and be skipped, leaking PHI.
const originals = new WeakMap();

// Weak registry of every element the engine touched, so deactivation restores
// subtrees that are currently detached (KeepAlive caches) without retaining
// elements the application has thrown away. The prune threshold doubles past
// the live size after each sweep, so a page with thousands of live tagged
// elements is swept O(log n) times instead of on every registration.
const touched = new Set();
const TOUCHED_PRUNE_AT = 5000;
let touchedPruneAt = TOUCHED_PRUNE_AT;

function registerTouched(el) {
  if (originals.has(el)) return;
  touched.add(new WeakRef(el));
  if (touched.size > touchedPruneAt) {
    for (const ref of touched) if (!ref.deref()) touched.delete(ref);
    touchedPruneAt = Math.max(TOUCHED_PRUNE_AT, touched.size * 2);
  }
}

// Elements whose masking depends on learned name tokens and must be recomputed
// when the map grows.
const rescanList = new Set();
const RESCAN_PRUNE_AT = 1000;

// Attributes that can carry real data to the user (tooltips, screen readers,
// truncation hovers, autofilled inputs).
const SENSITIVE_ATTRS = ['title', 'aria-label', 'alt', 'placeholder'];

// Neutral replacements for attributes on redacted content. Hover and assistive
// technology get a label, never the underlying prose.
const NEUTRAL_ATTR = 'Redacted';

const CONTROL_ATTRS = ['readonly', 'tabindex', 'aria-hidden', 'autocomplete'];
const CONTROL_WRITES = {
  readonly: '',
  tabindex: '-1',
  'aria-hidden': 'true',
  autocomplete: 'off',
};
// Identifying search boxes (`data-demo-search`) stay editable: the presenter
// types, and demoProtectedFields / the grid filter guard keep only what they
// typed (utils/demoSearchInput). The engine only turns off the browser's
// autocomplete, whose dropdown of past entries is outside the page, and
// leaves the placeholder readable.
const SEARCH_CONTROL_WRITES = { autocomplete: 'off' };
const NON_TEXT_INPUT_TYPES = new Set([
  'checkbox', 'radio', 'button', 'submit', 'reset', 'hidden', 'file', 'image',
  'range', 'color',
]);

let failureHandler = null;

// The controller registers a handler so failures surface in presenter state.
// Reports carry a stage and a count only -- never element content.
export function setSpoofFailureHandler(fn) {
  failureHandler = typeof fn === 'function' ? fn : null;
}

function reportFailure(stage, count = 1) {
  if (!failureHandler) return;
  try {
    failureHandler({ stage, count });
  } catch {
    /* a broken handler must not break masking */
  }
}

function closestSensitive(node) {
  const el = node?.nodeType === 1 ? node : node?.parentElement;
  return el?.closest?.('.sensitive') || null;
}

function isTextControl(el) {
  if (el.tagName === 'TEXTAREA') return true;
  if (el.tagName !== 'INPUT') return false;
  return !NON_TEXT_INPUT_TYPES.has((el.getAttribute('type') || 'text').toLowerCase());
}

function isSearchControl(el) {
  return Boolean(el?.hasAttribute?.('data-demo-search')) && isTextControl(el);
}

// Text nodes this element is responsible for: descendants whose nearest
// .sensitive ancestor is the element itself. Nested .sensitive elements own
// their own text, which prevents double-spoofing.
function ownedTextNodes(el) {
  const out = [];
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
  let node;
  while ((node = walker.nextNode())) {
    if (!node.nodeValue || !node.nodeValue.trim()) continue;
    const parent = node.parentElement;
    if (!parent || parent.tagName === 'TEXTAREA' || parent.tagName === 'SCRIPT' || parent.tagName === 'STYLE') continue;
    if (parent.closest('.sensitive') !== el) continue;
    if (parent.closest(`[${OVERLAY_ATTR}]`)) continue;
    out.push(node);
  }
  return out;
}

function ownedDescendants(el, selector) {
  const out = [];
  el.querySelectorAll(selector).forEach((d) => {
    if (d.parentElement?.closest('.sensitive') === el && !d.classList.contains('sensitive')) {
      out.push(d);
    }
  });
  return out;
}

function maskText(text) {
  return text.replace(/\S/g, 'x');
}

// Blur fallback for text we cannot safely spoof. A plain CSS blur is partially
// legible when a viewer zooms or pauses a high-res recording, so the styles.css
// rule for this attribute also drops the glyphs to transparent under a heavy
// text-shadow smudge. The text nodes underneath are replaced with a mask as
// well, so selection, zoom and screen readers find nothing either. An attribute
// rather than inline styles, so the element's own inline color/filter survive
// masking and come back intact when Demo Mode ends.
const REDACTED_ATTR = 'data-demo-redacted';

function applyBlur(el) {
  if (!el.hasAttribute(REDACTED_ATTR)) el.setAttribute(REDACTED_ATTR, '');
}

function clearBlur(el) {
  if (el.hasAttribute(REDACTED_ATTR)) el.removeAttribute(REDACTED_ATTR);
}

// Current app-intended value of a text node, seeing through our own writes.
function originalOf(node, byNode) {
  const rec = byNode.get(node);
  if (rec && node.nodeValue === rec.written) return rec.original;
  return node.nodeValue;
}

// The element's declared kind: data-spoof-kind, or a `spoof-kind-<kind>`
// class (how ag-grid columns declare one through cellClass). null = untyped.
function kindOf(el) {
  const attr = el.getAttribute?.('data-spoof-kind');
  if (attr != null) return attr.trim();
  for (const c of el.classList || []) {
    if (c.startsWith(KIND_CLASS_PREFIX)) return c.slice(KIND_CLASS_PREFIX.length);
  }
  return null;
}

// The record scope an element's dates belong to: a `demo-record-<key>` class
// on the element itself (ag-grid cells), else the nearest data-demo-record.
function recordOf(el) {
  for (const c of el.classList || []) {
    if (c.startsWith(RECORD_CLASS_PREFIX)) return c.slice(RECORD_CLASS_PREFIX.length);
  }
  return el.closest?.('[data-demo-record]')?.getAttribute('data-demo-record') || null;
}

// Decide replacements for an element's owned text. `values` is null when
// anything in it cannot be proven masked, which means the whole element is
// redacted. `passthrough[i]` marks text deliberately shown as-is (an age, a
// sex), so an unchanged value there is not mistaken for a failed spoof.
function planSpoof(el, nodes, originalsList) {
  if (!nodes.length) return { values: [], passthrough: [], unstable: false };
  const kind = kindOf(el);
  const record = kind === 'date' ? recordOf(el) : null;
  const leaf = nodes.every((n) => n.parentNode === el);
  if (leaf) {
    const combined = originalsList.join('');
    const r = classifyAndSpoof(combined, kind, record);
    if (r.value === null) return { values: null, unstable: false };
    const unstable = !r.stable && (/\s/.test(combined.trim()) || combined.length > 20);
    if (r.passthrough) {
      return { values: [...originalsList], passthrough: originalsList.map(() => true), unstable };
    }
    return {
      values: originalsList.map((_, i) => (i === 0 ? r.value : '')),
      passthrough: originalsList.map((_, i) => i > 0),
      unstable,
    };
  }
  const values = [];
  const passthrough = [];
  let unstable = false;
  for (const original of originalsList) {
    const r = classifyAndSpoof(original, kind, record);
    if (r.value === null) return { values: null, unstable: false };
    if (!r.stable && /\s/.test(original.trim())) unstable = true;
    values.push(r.value);
    passthrough.push(r.passthrough);
  }
  return { values, passthrough, unstable };
}

function writeAttr(el, entry, attr, desired) {
  const current = el.getAttribute(attr);
  const rec = entry.attrs[attr];
  const original = rec && current === rec.written ? rec.original : current;
  if (current !== desired) el.setAttribute(attr, desired);
  entry.attrs[attr] = { original, written: desired };
}

// Attributes on the element itself. `redact` neutralizes them; otherwise the
// aggressive detector spoofs them (the element is assumed to hold PHI), using
// the element's kind when it declares one.
function processOwnAttrs(el, entry, redact, kind, record) {
  for (const attr of SENSITIVE_ATTRS) {
    if (!el.hasAttribute(attr)) continue;
    if (attr === 'placeholder' && isSearchControl(el)) continue;
    const current = el.getAttribute(attr);
    const rec = entry.attrs[attr];
    const original = rec && current === rec.written ? rec.original : current;
    if (!original || !original.trim()) continue;
    let desired;
    if (redact) desired = NEUTRAL_ATTR;
    else {
      const spoofed = detectAndSpoof(original, kind, record);
      desired = spoofed === null ? NEUTRAL_ATTR : applyBrand(spoofed);
    }
    writeAttr(el, entry, attr, desired);
  }
}

// Descendant attributes are part of the owner's content: a truncation hover
// or icon label inside a tagged cell usually repeats the cell's value. They get
// the owner's policy (neutralized when redacting, the aggressive detector
// otherwise), never the conservative document-wide pass, which leaves text it
// does not recognize (a practice or facility name) readable.
function processDescendantAttrs(el, redact, kind, record) {
  const selector = SENSITIVE_ATTRS.map((a) => `[${a}]`).join(',');
  for (const d of ownedDescendants(el, selector)) {
    const entry = originals.get(d) || { attrs: {} };
    if (!entry.attrs) entry.attrs = {};
    let changed = false;
    for (const attr of SENSITIVE_ATTRS) {
      if (!d.hasAttribute(attr)) continue;
      if (attr === 'placeholder' && isSearchControl(d)) continue;
      const current = d.getAttribute(attr);
      const rec = entry.attrs[attr];
      const original = rec && current === rec.written ? rec.original : current;
      if (!original || !original.trim()) continue;
      let desired = NEUTRAL_ATTR;
      if (!redact) {
        const spoofed = detectAndSpoof(original, kind, record);
        if (spoofed !== null) desired = applyBrand(spoofed);
      }
      writeAttr(d, entry, attr, desired);
      changed = true;
    }
    if (changed) {
      registerTouched(d);
      originals.set(d, entry);
      d.setAttribute('data-spoofed', '');
    }
  }
}

// Sensitive form controls are shown as a protected read-only representation:
// the value property is never written, so the business model cannot receive a
// fake value and async model updates or paste cannot reveal a raw one. CSS
// masks the value; these attributes stop focus, editing and assistive-
// technology readout. A record field (a control with a value) additionally
// gets a display overlay showing a fake of that value (see below). Search
// boxes (data-demo-search) stay editable and get no overlay.
function protectControl(control, entry, kind, hardRedact) {
  const writes = isSearchControl(control) ? SEARCH_CONTROL_WRITES : CONTROL_WRITES;
  if (!entry.controls) entry.controls = new Map();
  let saved = entry.controls.get(control);
  if (!saved) {
    saved = {};
    for (const attr of CONTROL_ATTRS) {
      saved[attr] = control.hasAttribute(attr) ? control.getAttribute(attr) : null;
    }
    entry.controls.set(control, saved);
  } else {
    for (const attr of CONTROL_ATTRS) {
      const current = control.hasAttribute(attr) ? control.getAttribute(attr) : null;
      if (current !== CONTROL_WRITES[attr]) saved[attr] = current;
    }
  }
  // A control that became a search box after it was protected (ag-grid
  // enables a filter input after rendering it) gets its attributes back.
  if (writes === SEARCH_CONTROL_WRITES && control.hasAttribute('data-demo-protected')) {
    releaseControl(control, saved);
  }
  for (const attr of CONTROL_ATTRS) {
    if (!(attr in writes)) continue;
    if (control.getAttribute(attr) !== writes[attr]) {
      control.setAttribute(attr, writes[attr]);
    }
  }
  if (writes === SEARCH_CONTROL_WRITES) {
    control.removeAttribute('data-demo-protected');
  } else if (!control.hasAttribute('data-demo-protected')) {
    control.setAttribute('data-demo-protected', '');
  }
  syncOverlay(control, kindOf(control) ?? kind, hardRedact || control.hasAttribute('data-demo-blur'));
}

function releaseControl(control, saved) {
  removeOverlay(control);
  for (const attr of CONTROL_ATTRS) {
    if (control.getAttribute(attr) !== CONTROL_WRITES[attr]) continue;
    if (saved[attr] === null) control.removeAttribute(attr);
    else control.setAttribute(attr, saved[attr]);
  }
  control.removeAttribute('data-demo-protected');
}

function processControls(el, entry, kind, hardRedact) {
  const controls = [];
  if (isTextControl(el)) controls.push(el);
  for (const c of ownedDescendants(el, 'input, textarea')) {
    if (isTextControl(c)) controls.push(c);
  }
  const present = new Set(controls);
  if (entry.controls) {
    for (const [c, saved] of entry.controls) {
      if (!present.has(c)) {
        if (c.isConnected && el.contains(c)) releaseControl(c, saved);
        else removeOverlay(c);
        entry.controls.delete(c);
      }
    }
  }
  controls.forEach((c) => protectControl(c, entry, kind, hardRedact));
  return controls.length;
}

// --- Display overlays for protected record fields ---
//
// A protected control keeps its real value (never written, never submitted
// differently) and stays transparent, read-only and aria-hidden. So an edit
// form does not read as a column of blank boxes, a sibling element is laid
// over each control that holds a value, showing:
//   - a fake of a short value (the control's or owner's data-spoof-kind, else
//     the heuristic ladder);
//   - the redaction treatment over fixed placeholder text for prose, for a
//     hard-redacted (data-demo-blur) control, or when no coherent fake exists;
//   - nothing (the hatched control) for an empty value, a search box, or a
//     date that is not a typed DOB (non-DOB dates are product-pending, §1C).
// The overlay is display-only: pointer-events none, outside any form value,
// tracked through programmatic `value` writes, removed on exit and when the
// control goes away.
const OVERLAY_ATTR = 'data-demo-overlay';
const overlays = new Map(); // control -> { overlay, text, resize, unhook }
const DATE_INPUT_TYPES = new Set(['date', 'datetime-local', 'month', 'week', 'time']);
const OVERLAY_MASK_SHORT = 'xxxxxx xxxxx xxxx';
const OVERLAY_MASK_PROSE = 'xxxxx xxxx xxxxxxx xxx xxxxxx xxxx xxxxx xxxxxxxx xx xxxxx xxx xxxxxx xxxx';
let overlayResizeBound = false;

function valueDescriptor(control) {
  const proto = control.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement?.prototype : window.HTMLInputElement?.prototype;
  return proto ? Object.getOwnPropertyDescriptor(proto, 'value') : null;
}

function controlValue(control) {
  const desc = valueDescriptor(control);
  return desc?.get ? desc.get.call(control) : control.value;
}

function toDisplayDate(iso) {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : iso;
}

function overlayContent(control, kind, hardRedact) {
  const type = (control.getAttribute('type') || 'text').toLowerCase();
  // A search box stays editable and readable during a demo: the presenter
  // sees what they type, so nothing is laid over it.
  if (type === 'search' || isSearchControl(control)) return null;
  const value = controlValue(control);
  if (value == null || !String(value).trim()) return null;
  const text = String(value);
  const isProse = control.tagName === 'TEXTAREA' || text.length > 60 || /\n/.test(text);
  if (hardRedact) return { text: isProse ? OVERLAY_MASK_PROSE : OVERLAY_MASK_SHORT, redacted: true, prose: isProse };
  if (DATE_INPUT_TYPES.has(type) || (!kind && parseDate(text.trim()))) {
    // A DOB follows the DOB policy; an event date moves by its record's
    // offset and stays hatched when it has no record scope.
    const record = kind === 'date' ? recordOf(control) : null;
    if (kind !== 'dob' && !record) return null;
    const fake = spoofTyped(kind, text, record);
    if (!fake) return null;
    return { text: type === 'date' ? toDisplayDate(fake) : fake, redacted: false, prose: false };
  }
  if (isProse) return { text: OVERLAY_MASK_PROSE, redacted: true, prose: true };
  const fake = detectAndSpoof(text, kind);
  if (fake === null || !fake.trim()) return { text: OVERLAY_MASK_SHORT, redacted: true, prose: false };
  return { text: applyBrand(fake), redacted: false, prose: false };
}

function positionOverlay(control, rec) {
  const { overlay } = rec;
  const cs = getComputedStyle(control);
  const s = overlay.style;
  const borderL = parseFloat(cs.borderLeftWidth) || 0;
  const borderT = parseFloat(cs.borderTopWidth) || 0;
  let left = control.offsetLeft;
  let top = control.offsetTop;
  if (overlay.offsetParent !== control.offsetParent) {
    // Different containing blocks (e.g. a static table cell): measure.
    s.left = '0px';
    s.top = '0px';
    const origin = overlay.getBoundingClientRect();
    const box = control.getBoundingClientRect();
    left = box.left - origin.left;
    top = box.top - origin.top;
  }
  s.left = `${left + borderL}px`;
  s.top = `${top + borderT}px`;
  s.width = `${control.clientWidth}px`;
  s.height = `${control.clientHeight}px`;
  s.padding = cs.padding;
  s.fontFamily = cs.fontFamily;
  s.fontSize = cs.fontSize;
  s.fontWeight = cs.fontWeight;
  s.lineHeight = cs.lineHeight;
  s.textAlign = cs.textAlign;
  s.borderRadius = cs.borderRadius;
}

function repositionAllOverlays() {
  for (const [control, rec] of overlays) {
    if (control.isConnected) positionOverlay(control, rec);
  }
}

// Programmatic assignment (`el.value = x`, which v-model does) fires no event
// and no mutation, so the control's own `value` setter is wrapped while it is
// protected. The wrapper only refreshes the overlay; the value itself is set
// exactly as before.
function hookValue(control) {
  const desc = valueDescriptor(control);
  if (!desc?.set || Object.prototype.hasOwnProperty.call(control, 'value')) return null;
  try {
    Object.defineProperty(control, 'value', {
      configurable: true,
      enumerable: desc.enumerable,
      get() {
        return desc.get.call(this);
      },
      set(v) {
        desc.set.call(this, v);
        queueMicrotask(() => refreshOverlay(control));
      },
    });
  } catch {
    return null;
  }
  return () => {
    try {
      delete control.value;
    } catch {
      /* nothing to undo */
    }
  };
}

function refreshOverlay(control) {
  const rec = overlays.get(control);
  if (!rec || !control.isConnected) return;
  try {
    syncOverlay(control, rec.kind, rec.hardRedact);
  } catch {
    reportFailure('element');
  }
}

function syncOverlay(control, kind, hardRedact) {
  const content = overlayContent(control, kind, hardRedact);
  if (!content) {
    removeOverlay(control);
    return;
  }
  let rec = overlays.get(control);
  if (!rec || rec.overlay.previousSibling !== control || !rec.overlay.isConnected) {
    if (rec) removeOverlay(control);
    const overlay = document.createElement('div');
    overlay.setAttribute(OVERLAY_ATTR, '');
    overlay.style.cssText =
      'position:absolute;z-index:1;pointer-events:none;overflow:hidden;box-sizing:border-box;' +
      'display:flex;background:rgba(255,255,255,0.92);color:#374151;';
    const text = document.createElement('span');
    overlay.appendChild(text);
    control.after(overlay);
    rec = { overlay, text, resize: null, unhook: hookValue(control) };
    if (typeof ResizeObserver === 'function') {
      rec.resize = new ResizeObserver(() => positionOverlay(control, rec));
      rec.resize.observe(control);
    }
    if (!overlayResizeBound && typeof window !== 'undefined') {
      window.addEventListener('resize', repositionAllOverlays);
      overlayResizeBound = true;
    }
    overlays.set(control, rec);
  }
  rec.kind = kind;
  rec.hardRedact = hardRedact;
  const { overlay, text } = rec;
  overlay.style.alignItems = content.prose ? 'flex-start' : 'center';
  overlay.style.whiteSpace = content.prose ? 'normal' : 'nowrap';
  if (text.textContent !== content.text) text.textContent = content.text;
  if (content.redacted) applyBlur(text);
  else clearBlur(text);
  positionOverlay(control, rec);
}

function removeOverlay(control) {
  const rec = overlays.get(control);
  if (!rec) return;
  overlays.delete(control);
  rec.resize?.disconnect();
  rec.unhook?.();
  rec.overlay.remove();
}

// Controls that left the document (unmounted, or a cached subtree detached)
// lose their overlay; a reattached one gets a new overlay when it is
// reprocessed.
function sweepOverlays() {
  for (const control of [...overlays.keys()]) {
    if (!control.isConnected) removeOverlay(control);
  }
}

function removeAllOverlays() {
  for (const control of [...overlays.keys()]) removeOverlay(control);
  if (overlayResizeBound) {
    window.removeEventListener('resize', repositionAllOverlays);
    overlayResizeBound = false;
  }
}

function isInOverlay(node) {
  const el = node?.nodeType === 1 ? node : node?.parentElement;
  return Boolean(el?.closest?.(`[${OVERLAY_ATTR}]`));
}

// Every owned text node must now hold exactly what the engine wrote. Anything
// else means unprocessed content is on screen.
function verify(el, entry) {
  const byNode = new Map(entry.nodes.map((r) => [r.node, r]));
  for (const node of ownedTextNodes(el)) {
    const rec = byNode.get(node);
    if (!rec || node.nodeValue !== rec.written) return false;
  }
  return true;
}

function cover(el) {
  el.setAttribute('data-demo-covered', '');
  el.removeAttribute('data-spoofed');
}

// data-demo-blur forces redaction on its element and on any tagged element
// inside it: a child tagged to keep its value apart from its siblings must not
// come out of a hard-redacted container spoofed (or passed through).
function isHardRedacted(el) {
  return el.hasAttribute('data-demo-blur') || Boolean(el.parentElement?.closest('.sensitive[data-demo-blur]'));
}

// --- Accepted prose rewrites ---
//
// DemoText renders a server rewrite of its prose into its own element and then
// registers the exact text nodes it validated. That registration lives here,
// never in a DOM attribute. An element whose owned text nodes are exactly the
// registered nodes, each still holding its registered value, is shown as
// rendered: only text substitution is skipped. Its own and descendant hover
// and label attributes are still neutralized, links still go through the href
// rewriter, and an inherited hard redaction still wins. Anything else (a late
// write, an extra child, a control) drops the registration and the element is
// redacted as before. Deactivation retires every registration (epoch).
const rewrites = new WeakMap(); // el -> { values: Map<Text, string>, epoch }
let rewriteEpoch = 0;

function hasOwnedControls(el) {
  if (isTextControl(el)) return true;
  return ownedDescendants(el, 'input, textarea').some(isTextControl);
}

// What the application last put in each node, seeing through the engine's
// own writes (a rewrite that equals the source leaves the masked node as it
// was; the rendered value is still the source).
function appValues(el, nodes) {
  const byNode = new Map((originals.get(el)?.nodes || []).map((r) => [r.node, r]));
  return nodes.map((n) => originalOf(n, byNode));
}

function acceptedRewrite(el, nodes) {
  const reg = rewrites.get(el);
  if (!reg) return false;
  const values = appValues(el, nodes);
  const valid =
    reg.epoch === rewriteEpoch &&
    !el.parentElement?.closest('.sensitive[data-demo-blur]') &&
    nodes.length === reg.values.size &&
    nodes.every((n, i) => reg.values.get(n) === values[i]) &&
    !hasOwnedControls(el);
  if (!valid) rewrites.delete(el);
  return valid;
}

function processRewrite(el, entry, nodes, kind) {
  const reg = rewrites.get(el);
  entry.mode = 'rewrite';
  entry.kind = kind;
  entry.record = recordOf(el);
  entry.nodes = nodes.map((node) => {
    const value = reg.values.get(node);
    if (node.nodeValue !== value) node.nodeValue = value;
    return { node, original: value, written: value };
  });
  clearBlur(el);
  rescanList.delete(el);
  processOwnAttrs(el, entry, true, kind, null);
  processDescendantAttrs(el, true, kind, null);
  originals.set(el, entry);
  if (!verify(el, entry)) {
    cover(el);
    reportFailure('element');
    return;
  }
  el.removeAttribute('data-demo-covered');
  el.setAttribute('data-spoofed', '');
}

// Registers the rewrite DemoText just rendered into `el`. `expected` is the
// list of non-blank text node values its renderer produces for the validated
// rewrite; what the application rendered into the element's owned text nodes
// (seeing through the engine's own masks) must match it exactly, in order.
// The element is reprocessed at once (with any mutations still queued), so
// the old redaction is removed in the same pass that accepts the rewrite.
// Returns whether the rewrite is now shown.
export function registerDemoRewrite(el, expected) {
  if (!observer || !el?.isConnected || !el.classList?.contains('sensitive')) return false;
  const nodes = ownedTextNodes(el);
  const values = appValues(el, nodes);
  const matches =
    Array.isArray(expected) &&
    expected.length > 0 &&
    nodes.length === expected.length &&
    values.every((v, i) => v === expected[i]) &&
    !hasOwnedControls(el);
  if (matches) rewrites.set(el, { values: new Map(nodes.map((n, i) => [n, values[i]])), epoch: rewriteEpoch });
  else rewrites.delete(el);
  reprocessNow(el);
  return originals.get(el)?.mode === 'rewrite';
}

export function clearDemoRewrite(el) {
  if (!el || !rewrites.delete(el)) return;
  if (observer && el.isConnected) reprocessNow(el);
}

export function isDemoRewriteShown(el) {
  return Boolean(el && rewrites.has(el) && originals.get(el)?.mode === 'rewrite');
}

function processElementUnsafe(el) {
  const prior = originals.get(el);
  const entry = prior || { attrs: {}, nodes: [] };
  if (!entry.attrs) entry.attrs = {};
  registerTouched(el);

  const nodes = ownedTextNodes(el);
  const kind = kindOf(el);
  if (acceptedRewrite(el, nodes)) {
    processRewrite(el, entry, nodes, kind);
    return;
  }

  const byNode = new Map((entry.nodes || []).map((r) => [r.node, r]));
  const originalsList = nodes.map((n) => originalOf(n, byNode));

  const hardRedact = isHardRedacted(el);
  let planned = hardRedact ? null : planSpoof(el, nodes, originalsList);
  // A spoof that left a value unchanged did not mask it (unless the value is
  // a placeholder or deliberately passed through).
  if (planned?.values) {
    const unchanged = planned.values.some(
      (p, i) => p === originalsList[i] && !planned.passthrough[i] && !isPlaceholderValue(originalsList[i].trim())
    );
    if (unchanged) planned = { values: null, unstable: false };
  }
  const redact = hardRedact || !planned?.values;

  const records = nodes.map((node, i) => {
    const original = originalsList[i];
    const written = redact ? maskText(original) : planned.values[i];
    if (node.nodeValue !== written) node.nodeValue = written;
    return { node, original, written };
  });

  entry.mode = redact ? 'redact' : 'spoof';
  entry.kind = kind;
  entry.record = recordOf(el);
  entry.nodes = records;
  if (redact) {
    applyBlur(el);
    rescanList.delete(el);
  } else {
    clearBlur(el);
    if (planned.unstable) rescanList.add(el);
    else rescanList.delete(el);
  }

  const record = kind === 'date' ? entry.record : null;
  processOwnAttrs(el, entry, redact, kind, record);
  processDescendantAttrs(el, redact, kind, record);
  processControls(el, entry, kind, hardRedact);

  originals.set(el, entry);
  if (!verify(el, entry)) {
    cover(el);
    reportFailure('element');
    return;
  }
  el.removeAttribute('data-demo-covered');
  el.setAttribute('data-spoofed', '');
}

function processElement(el) {
  // Never let one bad element abort the whole batch (which would leave the
  // observer disconnected and PHI flowing). A failure covers the element
  // opaquely and is reported rather than swallowed.
  try {
    processElementUnsafe(el);
  } catch {
    try {
      cover(el);
    } catch {
      /* element unusable */
    }
    reportFailure('element');
  }
}

// CSS selector for elements that can leak PHI through an attribute regardless of
// whether they are tagged `.sensitive` (e.g. an ag-grid cell's :title or an icon's
// aria-label). Scanned document-wide.
const ATTR_SELECTOR = SENSITIVE_ATTRS.map((a) => `[${a}]`).join(',');

function hasSensitiveAttr(el) {
  for (const attr of SENSITIVE_ATTRS) {
    if (el.hasAttribute?.(attr)) return true;
  }
  return false;
}

// Document-wide attribute-only scrubbing for elements that are NOT .sensitive.
// Only recognizable PHI is touched so app chrome is preserved. Elements inside a
// .sensitive element are handled by their owner instead.
function processAttrElement(el) {
  try {
    if (el.classList?.contains('sensitive')) return;
    if (closestSensitive(el.parentElement)) return;
    if (isInOverlay(el)) return;
    const entry = originals.get(el) || { attrs: {} };
    if (!entry.attrs) entry.attrs = {};
    let changed = false;
    for (const attr of SENSITIVE_ATTRS) {
      if (!el.hasAttribute(attr)) continue;
      const current = el.getAttribute(attr);
      if (!current || !current.trim()) continue;
      const rec = entry.attrs[attr];
      if (rec && current === rec.written) continue;
      const spoofed = applyBrand(spoofKnownPhi(current));
      if (spoofed === current) continue;
      entry.attrs[attr] = { original: current, written: spoofed };
      el.setAttribute(attr, spoofed);
      changed = true;
    }
    if (changed || Object.keys(entry.attrs).length > 0) {
      registerTouched(el);
      originals.set(el, entry);
      el.setAttribute('data-spoofed', '');
    }
  } catch {
    reportFailure('element');
  }
}

// Brand mask for untagged text. Tagged text is spoofed or redacted by its
// owner, so only text outside .sensitive is rewritten here; the original comes
// back on exit only where the branded value is still in place.
const BRAND_SKIP = '.sensitive, script, style, textarea, [data-demo-overlay]';
const brandedNodes = new WeakMap();
const brandedRefs = new Set();

function brandTextNode(node) {
  const value = node.nodeValue;
  if (!value || !BRAND_TEST_RE.test(value)) return;
  const parent = node.parentElement;
  if (!parent || parent.closest(BRAND_SKIP)) return;
  const branded = applyBrand(value);
  if (branded === value) return;
  if (!brandedNodes.has(node)) brandedRefs.add(new WeakRef(node));
  brandedNodes.set(node, { original: value, written: branded });
  node.nodeValue = branded;
}

function brandSubtree(root) {
  if (!root) return;
  if (root.nodeType === 3) {
    brandTextNode(root);
    return;
  }
  if (root.nodeType !== 1 || !BRAND_TEST_RE.test(root.textContent || '')) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let node;
  while ((node = walker.nextNode())) brandTextNode(node);
}

function restoreBrand() {
  for (const ref of brandedRefs) {
    const node = ref.deref();
    const rec = node && brandedNodes.get(node);
    if (rec && node.nodeValue === rec.written) node.nodeValue = rec.original;
    if (node) brandedNodes.delete(node);
  }
  brandedRefs.clear();
}

function processAllAttributes() {
  document.querySelectorAll(ATTR_SELECTOR).forEach(processAttrElement);
}

// Links whose destination would expose an identifier (a patient page URL shows
// in the status bar on hover, and in the address bar of a tab it opens) are
// rewritten while Demo Mode is on: the controller registers a rewriter that
// returns the same link with a stand-in token for the identifier (hover,
// middle-click, new tab and keyboard activation keep working and show only
// the token), false for a link that cannot carry a token (its href is
// removed), or null for an ordinary link. The original is kept in its own weak
// map so re-tagging or resetting the element never loses it, and comes back on
// exit only if the application has not since written a new href.
let hrefRewriter = null;
const rewrittenLinks = new WeakMap(); // a -> { original, written | null }
const rewrittenLinkRefs = new Set();
const LINK_MARK = 'data-demo-link-hidden';
const LINK_TOKEN_MARK = 'data-demo-link-token';
const LINK_PRUNE_AT = 5000;
let linkPruneAt = LINK_PRUNE_AT;

export function setSensitiveHrefRewriter(fn) {
  hrefRewriter = typeof fn === 'function' ? fn : null;
}

function trackLink(el, rec) {
  if (!rewrittenLinks.has(el)) {
    rewrittenLinkRefs.add(new WeakRef(el));
    if (rewrittenLinkRefs.size > linkPruneAt) {
      for (const ref of rewrittenLinkRefs) if (!ref.deref()) rewrittenLinkRefs.delete(ref);
      linkPruneAt = Math.max(LINK_PRUNE_AT, rewrittenLinkRefs.size * 2);
    }
  }
  rewrittenLinks.set(el, rec);
}

function processLink(el) {
  if (!hrefRewriter || el.tagName !== 'A') return;
  try {
    const href = el.getAttribute('href');
    if (href === null) return;
    const prior = rewrittenLinks.get(el);
    if (prior && prior.written === href) return;
    const rewritten = hrefRewriter(href);
    if (rewritten === null || rewritten === undefined || rewritten === href) return;
    if (rewritten === false) {
      trackLink(el, { original: href, written: null });
      el.removeAttribute('href');
      el.removeAttribute(LINK_TOKEN_MARK);
      el.setAttribute(LINK_MARK, '');
      return;
    }
    trackLink(el, { original: href, written: rewritten });
    el.setAttribute('href', rewritten);
    el.removeAttribute(LINK_MARK);
    el.setAttribute(LINK_TOKEN_MARK, '');
  } catch {
    // A link the engine could not rewrite must not keep its identifying href.
    try {
      el.removeAttribute('href');
      el.setAttribute(LINK_MARK, '');
    } catch {
      /* element unusable */
    }
    reportFailure('element');
  }
}

function processAllLinks() {
  if (!hrefRewriter) return;
  document.querySelectorAll('a[href]').forEach(processLink);
}

function restoreLinks() {
  for (const ref of rewrittenLinkRefs) {
    const el = ref.deref();
    if (!el) continue;
    const rec = rewrittenLinks.get(el);
    if (rec) {
      const current = el.getAttribute('href');
      if (rec.written === null ? current === null : current === rec.written) {
        el.setAttribute('href', rec.original);
      }
    }
    el.removeAttribute(LINK_MARK);
    el.removeAttribute(LINK_TOKEN_MARK);
    rewrittenLinks.delete(el);
  }
  rewrittenLinkRefs.clear();
  linkPruneAt = LINK_PRUNE_AT;
}

// Put back what the application last intended, but only where the engine's own
// output is still in place. A value the application wrote after the last pass
// is newer than the stored snapshot and is left alone.
function restoreElement(el) {
  const saved = originals.get(el);
  if (!saved) return;
  for (const rec of saved.nodes || []) {
    if (rec.node.nodeValue === rec.written && rec.node.parentNode) {
      rec.node.nodeValue = rec.original;
    }
  }
  if (saved.mode === 'redact') clearBlur(el);
  for (const attr of SENSITIVE_ATTRS) {
    const rec = saved.attrs?.[attr];
    if (rec && el.getAttribute(attr) === rec.written) {
      el.setAttribute(attr, rec.original);
    }
  }
  if (saved.controls) {
    for (const [c, s] of saved.controls) releaseControl(c, s);
  }
  el.removeAttribute('data-spoofed');
  el.removeAttribute('data-demo-covered');
  originals.delete(el);
  rescanList.delete(el);
}

function processAll() {
  document.querySelectorAll('.sensitive').forEach(processElement);
}

// Recompute name-dependent elements now that more name tokens are known. The
// recomputation always starts from stored originals, so results cannot compound.
function rescanFreeText() {
  for (const el of [...rescanList]) {
    if (!el.isConnected || !originals.has(el)) {
      rescanList.delete(el);
      continue;
    }
    processElement(el);
  }
}

// Run a rescan if names were learned, looping until the map stops growing
// (a rescan can itself learn names from short free-text nodes). Bounded.
function rescanIfLearned(versionBefore) {
  if (nameMapVersion === versionBefore) return;
  let guard = 0;
  let v;
  do {
    v = nameMapVersion;
    rescanFreeText();
  } while (nameMapVersion !== v && ++guard < 5);
}

function restoreAll() {
  const seen = new Set();
  for (const ref of touched) {
    const el = ref.deref();
    if (!el || seen.has(el)) continue;
    seen.add(el);
    restoreElement(el);
  }
  document.querySelectorAll('[data-spoofed],[data-demo-covered]').forEach((el) => {
    if (seen.has(el)) return;
    restoreElement(el);
    el.removeAttribute('data-spoofed');
    el.removeAttribute('data-demo-covered');
  });
  touched.clear();
  touchedPruneAt = TOUCHED_PRUNE_AT;
}

let observer = null;

const OBSERVE_OPTS = {
  childList: true,
  subtree: true,
  characterData: true,
  attributes: true,
  attributeFilter: [...SENSITIVE_ATTRS, 'class', 'data-demo-blur', 'data-demo-search', 'data-spoof-kind', 'data-demo-record', 'href', ...CONTROL_ATTRS],
};

// Collect the elements affected by a batch of mutations.
//   sensitive: owners whose content, policy or controls changed (full pass)
//   release:   elements that stopped being .sensitive (restore)
//   reset:     owners whose ownership or redaction policy changed (restore,
//              then full pass from the application's current content)
//   attrEls:   untagged elements whose hover/label attributes changed
function collectTargets(mutations, t) {
  for (const m of mutations) {
    // Display overlays are the engine's own output.
    if (isInOverlay(m.target)) continue;
    if (m.type === 'childList') {
      const changed = [...m.addedNodes, ...m.removedNodes];
      if (changed.length && changed.every((n) => n.nodeType === 1 && n.hasAttribute(OVERLAY_ATTR))) continue;
      // The container whose children changed: its nearest sensitive ancestor
      // owns whatever was added or removed, including untagged replacements.
      const owner = closestSensitive(m.target);
      if (owner) t.sensitive.add(owner);
      for (const n of m.addedNodes) {
        if (n.nodeType === 1 && n.hasAttribute(OVERLAY_ATTR)) continue;
        if (BRAND_TEST_RE.test(n.nodeType === 3 ? n.nodeValue : n.textContent || '')) t.branded.add(n);
        if (n.nodeType !== 1) continue;
        if (n.classList?.contains('sensitive')) t.sensitive.add(n);
        n.querySelectorAll?.('.sensitive').forEach((e) => t.sensitive.add(e));
        if (hasSensitiveAttr(n)) t.attrEls.add(n);
        n.querySelectorAll?.(ATTR_SELECTOR).forEach((e) => t.attrEls.add(e));
        if (hrefRewriter) {
          if (n.tagName === 'A' && n.hasAttribute('href')) t.links.add(n);
          n.querySelectorAll?.('a[href]').forEach((e) => t.links.add(e));
        }
      }
    } else if (m.type === 'characterData') {
      if (BRAND_TEST_RE.test(m.target.nodeValue || '')) t.branded.add(m.target);
      const owner = closestSensitive(m.target);
      if (owner) t.sensitive.add(owner);
    } else if (m.type === 'attributes') {
      const el = m.target;
      const name = m.attributeName;
      if (name === 'href') {
        t.links.add(el);
        continue;
      }
      if (name === 'class') {
        const isSensitive = el.classList.contains('sensitive');
        const wasProcessed = originals.has(el) && originals.get(el).mode !== undefined;
        if (isSensitive === wasProcessed) {
          // A kind class added or removed changes how the text is spoofed.
          if (isSensitive && (originals.get(el).kind !== kindOf(el) || originals.get(el).record !== recordOf(el))) {
            t.reset.add(el);
          }
          continue;
        }
        const outer = closestSensitive(el.parentElement);
        if (isSensitive) {
          if (outer) t.reset.add(outer);
          t.sensitive.add(el);
        } else {
          t.release.add(el);
          if (outer) t.reset.add(outer);
        }
      } else if (name === 'data-demo-record') {
        // Dates inside a record scope move by that record's offset.
        if (el.classList.contains('sensitive')) t.reset.add(el);
        el.querySelectorAll('.sensitive').forEach((d) => t.reset.add(d));
        el.querySelectorAll('[data-demo-protected]').forEach((c) => {
          const owner = closestSensitive(c);
          if (owner) t.reset.add(owner);
        });
      } else if (name === 'data-demo-blur' || name === 'data-spoof-kind') {
        if (el.classList.contains('sensitive')) {
          t.reset.add(el);
          // Tagged descendants inherit hard redaction.
          if (name === 'data-demo-blur') el.querySelectorAll('.sensitive').forEach((d) => t.reset.add(d));
        }
        else {
          // A protected control's own kind or redaction changed.
          const owner = closestSensitive(el);
          if (owner) t.sensitive.add(owner);
        }
      } else if (CONTROL_ATTRS.includes(name)) {
        if (el.hasAttribute('data-demo-protected')) {
          const owner = closestSensitive(el);
          if (owner) t.sensitive.add(owner);
        }
      } else {
        const owner = closestSensitive(el);
        if (owner) t.sensitive.add(owner);
        else t.attrEls.add(el);
      }
    }
  }
}

function pruneRescanList() {
  if (rescanList.size <= RESCAN_PRUNE_AT) return;
  for (const el of rescanList) if (!el.isConnected) rescanList.delete(el);
}

function applyTargets(t) {
  const version = nameMapVersion;
  t.release.forEach((el) => {
    restoreElement(el);
  });
  t.reset.forEach((el) => {
    if (!el.isConnected || !el.classList.contains('sensitive')) return;
    restoreElement(el);
    t.sensitive.add(el);
  });
  t.sensitive.forEach((el) => {
    if (el.isConnected && el.classList.contains('sensitive')) processElement(el);
  });
  t.attrEls.forEach(processAttrElement);
  t.links.forEach(processLink);
  rescanIfLearned(version);
  t.branded.forEach(brandSubtree);
  pruneRescanList();
}

function emptyTargets() {
  return {
    sensitive: new Set(),
    release: new Set(),
    reset: new Set(),
    attrEls: new Set(),
    links: new Set(),
    branded: new Set(),
  };
}

function observe() {
  observer.observe(document.body, OBSERVE_OPTS);
}

// MutationObserver callbacks run as a microtask BEFORE the browser paints, so
// processing here (rather than in a deferred rAF) replaces real data before it
// can flash on screen. We disconnect while applying our own DOM writes and drain
// the resulting records so we never re-process our own output.
function flush(mutations) {
  flushWith(mutations, null);
}

// Processes `el` now, together with every mutation still queued for the
// observer (taking them must not drop them).
function reprocessNow(el) {
  if (!observer) return;
  flushWith(observer.takeRecords(), el);
}

function flushWith(mutations, extra) {
  if (overlays.size) {
    observer.disconnect();
    try {
      sweepOverlays();
    } finally {
      observer.takeRecords();
      observe();
    }
  }
  const t = emptyTargets();
  collectTargets(mutations, t);
  if (extra) t.sensitive.add(extra);
  if (!t.sensitive.size && !t.release.size && !t.reset.size && !t.attrEls.size && !t.links.size && !t.branded.size) return;
  observer.disconnect();
  // Always re-observe, even if processing throws -- otherwise the observer stays
  // disconnected and PHI renders unspoofed for the rest of the session.
  try {
    applyTargets(t);
  } catch {
    reportFailure('flush');
  } finally {
    try {
      observer.takeRecords();
      observe();
    } catch {
      reportFailure('observer');
    }
  }
}

export function isSpoofActive() {
  return observer !== null;
}

export function activateSpoof() {
  if (observer) return { ok: true };
  try {
    const version = nameMapVersion;
    processAll();
    processAllAttributes();
    processAllLinks();
    rescanIfLearned(version);
    brandSubtree(document.body);
    observer = new MutationObserver(flush);
    observe();
    return { ok: true };
  } catch {
    // Activation threw before the observer was safely attached -- subsequent
    // renders would flow unspoofed. Tear down any partial state and report it;
    // the controller turns Demo Mode off and warns the presenter.
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    reportFailure('activation');
    return { ok: false };
  }
}

export function deactivateSpoof() {
  if (observer) {
    observer.takeRecords();
    observer.disconnect();
    observer = null;
  }
  restoreAll();
  removeAllOverlays();
  restoreLinks();
  restoreBrand();
  rescanList.clear();
  rewriteEpoch += 1;
  const hadNames = nameTokenRoles.size > 0;
  nameTokenRoles.clear();
  const hadOrgs = orgFakes.size > 0;
  orgFakes.clear();
  nameTokenRegex = null;
  nameTokenDirty = false;
  nameMapVersion = 0;
  nameMapChangedAt = 0;
  if (hadNames || hadOrgs) notifyNameMapChange();
}

// Whether masking is requested or running. Reads the reactive controller state,
// so a computed property or render function that calls it re-evaluates when
// Demo Mode changes. Fails closed if the state module is unavailable.
export function isDemoMode() {
  return store.getters['demoMode/isDemoMode'] !== false;
}

export { exportBlockedInDemoMode } from '@/utils/demoEgress';
